export type TranslationKey = keyof typeof translations.en

export const translations = {
    en: {
        // Common
        'common.loading': 'Loading…',
        'common.loadingData': 'Loading data…',
        'common.error': 'Error',
        'common.networkError': 'Network error, please retry',
        'common.success': 'Success',
        'common.cancel': 'Cancel',
        'common.save': 'Save',
        'common.saveChanges': 'Save Changes',
        'common.saving': 'Saving...',
        'common.delete': 'Delete',
        'common.edit': 'Edit',
        'common.close': 'Close',
        'common.submit': 'Submit',
        'common.next': 'Next',
        'common.previous': 'Previous',
        'common.show': 'Show',
        'common.hide': 'Hide',
        'common.back': 'Back',
        'common.continue': 'Continue',
        'common.download': 'Download',
        'common.downloading': 'Downloading...',
        'common.share': 'Share',
        'common.search': 'Search',
        'common.clearSearch': 'Clear search',
        'common.filter': 'Filter',
        'common.viewAll': 'View All',
        'common.noResults': 'No results found',
        'common.tryAgain': 'Try again',
        'common.readMore': 'Read More',
        'common.untitled': 'Untitled',
        'common.sortBy': 'Sort by',
        'common.sortByLabel': 'Sort By :',
        'common.searchByName': 'Search By Name',
        'common.searchByNameShort': 'Search by name',
        'common.recentlyAdded': 'Recently added',
        'common.mostPopular': 'Most popular',
        'common.oldestFirst': 'Oldest first',
        'common.optional': 'Optional',
        'common.total': 'Total',
        'common.progress': 'Progress',
        'common.answered': 'Answered',
        'common.view': 'View',

        // Questionnaire
        'questionnaire.title': 'Complete your AIRIES profile',
        'questionnaire.badge': 'AI readiness check',
        'questionnaire.loading': 'Preparing your questionnaire...',
        'questionnaire.progress': 'Question {current} of {total}',
        'questionnaire.instruction': 'Choose the answer that fits best.',
        'questionnaire.other': 'Other',
        'questionnaire.otherPlaceholder': 'Write your answer',
        'questionnaire.finish': 'See My Score',
        'questionnaire.completed': 'Questionnaire completed.',
        'questionnaire.submitError': 'Could not submit answers. Try again.',
        'questionnaire.emptyTitle': 'No questions found',
        'questionnaire.emptyMessage': 'We could not load the questionnaire. Please try again.',
        
        // Blogs
        'blogs.title': 'Blogs',
        'blogs.subtitle': 'Discover insights, tips, and stories about AI and technology',
        'blogs.searchPlaceholder': 'Search blogs...',
        'blogs.readMore': 'Read More',
        'blogs.noBlogsFound': 'No blogs found',
        'blogs.notFound': 'Blog Not Found',
        'blogs.notFoundDesc': 'The blog post you are looking for does not exist.',
        'blogs.backToBlogs': 'Back to Blogs',
        'blogs.views': 'views',
        'blogs.likes': 'likes',
        'blogs.comments': 'comments',
        
        // Not Found (404)
        'notFound.title': 'Page Not Found',
        'notFound.description': "It seems the page you're looking for doesn't exist. Let's get you back on track.",
        'notFound.returnHome': 'Return to Homepage',
        'notFound.exploreTools': 'Explore Tools',
        
        // Navigation
        'nav.home': 'Home',
        'nav.tools': 'Tools',
        'nav.toolsExplained': 'Tools Explained',
        'nav.toolsMarketplace': 'Tools Marketplace',
        'nav.news': 'News',
        'nav.blogs': 'Blogs',
        'nav.newsAndBlogs': 'News & Blogs',
        'nav.courseContent': 'Course Content',
        'nav.events': 'Events',
        'nav.pricing': 'Pricing',
        'nav.universities': 'Universities',
        'nav.affiliate': 'Affiliate / Partner',
        'nav.overview': 'Overview',
        'nav.myCourses': 'My Courses',
        'nav.eventsWebinars': 'Events & Webinars',
        'nav.community': 'Community',
        'nav.rewards': 'Rewards',
        'nav.certificates': 'Certificates',
        'nav.profile': 'Profile',
        'nav.dashboard': 'Dashboard',
        'nav.signIn': 'Sign in',
        'nav.getStarted': 'Get started',
        'nav.logout': 'Logout',
        'nav.viewProfile': 'View profile',
        'nav.toggleMenu': 'Toggle menu',
        'nav.signedInAs': 'Signed in as',
        'nav.signedInToAiries': 'Signed in to Airies',
        'nav.contact': 'Contact',
        'nav.faq': 'FAQ',
        'nav.more': 'More',
        
        // Dashboard Overview
        'overview.welcomeBack': 'Welcome back',
        'overview.readyToContinue': 'Ready to continue your learning journey?',
        'overview.weeklyActivity': 'Weekly learning activity',
        'overview.hoursSpent': 'Hours spent learning this week',
        'overview.hour': 'hour',
        'overview.hours': 'hours',
        'overview.progressStatistics': 'Progress statistics',
        'overview.coursesCompleted': 'Courses Completed',
        'overview.totalCourses': 'Total Courses',
        'overview.certificates': 'Certificates',
        'overview.studyHours': 'Study hours',
        'overview.completed': 'Completed',
        'overview.continueLearning': 'Continue Learning',
        'overview.noActiveCourses': "You don't have any active courses yet. Start a course to see it here.",
        'overview.startLearning': 'Start Learning',
        'overview.failedToLoad': 'Failed to load dashboard',
        
        // Profile
        'profile.myProfile': 'My Profile',
        'profile.profileInfo': 'Profile Info',
        'profile.settings': 'Settings',
        'profile.profilePhoto': 'Profile Photo',
        'profile.firstName': 'First name',
        'profile.lastName': 'Last name',
        'profile.phone': 'Phone',
        'profile.email': 'Email',
        'profile.bio': 'Bio',
        'profile.bioPlaceholder': 'Please write a bio about yourself',
        'profile.university': 'University',
        'profile.company': 'Company',
        'profile.profileUpdated': 'Profile updated successfully!',
        'profile.failedToLoad': 'Failed to load profile',
        'profile.failedToSave': 'Failed to save profile',
        'profile.failedToUpload': 'Failed to upload avatar',
        'profile.language': 'Language',
        'profile.account': 'Account',
        'profile.changePassword': 'Change Password',
        'profile.currentPassword': 'Current Password',
        'profile.newPassword': 'New Password',
        'profile.confirmPassword': 'Confirm New Password',
        'profile.passwordUpdated': 'Password updated successfully!',
        'profile.failedToChangePassword': 'Failed to change password',
        'profile.allFieldsRequired': 'All fields are required',
        'profile.passwordsDoNotMatch': 'New passwords do not match',
        'profile.passwordTooShort': 'New password must be at least 6 characters long',
        'profile.passwordSame': 'New password must be different from current password',
        'profile.changingPassword': 'Changing...',
        'profile.sessions': 'Active sessions',
        'profile.clearAllDevices': 'Clear all devices',
        'profile.clearAllDevicesConfirm': 'Clear all devices?',
        'profile.clearAllDevicesConfirmDesc': 'This will sign you out from every device, including this one.',
        'profile.clearAndLogout': 'Clear & Logout',
        'profile.currentDevice': 'Current device',
        'profile.remove': 'Remove',
        'profile.subscription': 'Subscription',
        'profile.billing': 'Billing',
        'profile.alternateEmail': 'Alternate Email',
        'profile.alternateEmailSameAsPrimary': 'Alternate email cannot be the same as your primary email.',
        'profile.address': 'Address',
        'profile.addressLine1': 'Address Line 1',
        'profile.addressLine2': 'Address Line 2',
        'profile.city': 'City',
        'profile.state': 'State/Province',
        'profile.postalCode': 'Postal Code',
        'profile.country': 'Country',
        'profile.fieldsNoWhitespace': 'Fields cannot contain only spaces',
        'profile.searchPlaceholder': 'Search...',
        'subscription.currentPlan': 'Current Plan',
        'subscription.managedByCleverbridge': 'Subscription Managed by Cleverbridge',
        'subscription.availablePlans': 'Available Plans',
        'subscription.upgrade': 'Upgrade Now',
        'subscription.free': 'Free',
        'billing.noInvoices': 'No billing history found.',
        'billing.showing': 'Showing',
        'billing.of': 'of',
        'billing.page': 'Page',

        // Billing (dashboard profile)
        'billing.invoice': 'Invoice',
        'billing.date': 'Date',
        'billing.plan': 'Plan',
        'billing.amount': 'Amount',
        'billing.status': 'Status',
        'billing.action': 'Action',
        'billing.download': 'Download',
        'billing.managedByCleverbridge': 'Billing is managed by Cleverbridge.',
        'billing.downloadInvoice': 'Download Latest Invoice',
        'billing.cleverbridgeNoInvoice': 'Billing is managed by Cleverbridge. Download invoices from Manage Subscription in the Subscription tab.',
        
        // Courses
        'courses.myCourses': 'My Courses',
        'courses.allCourses': 'All Courses',
        'courses.inProgress': 'In Progress',
        'courses.completed': 'Completed',
        'courses.locked': 'Locked',
        'courses.noCourses': 'No courses found',
        'courses.loadingCourses': 'Loading courses…',
        'courses.startLearning': 'Start Learning',
        'courses.continueLearning': 'Continue Learning',
        'courses.lockedMessage': 'Please finish level 1',
        'courses.loadingCourse': 'Loading course…',
        'courses.courseNotFound': 'Course not found',
        'courses.overview': 'Overview',
        'courses.syllabus': 'Syllabus',
        'courses.content': 'Course content',
        'courses.loadingPdf': 'Loading PDF...',
        'courses.failedToLoadPdf': 'Failed to load PDF',
        'courses.failedToLoad': 'Failed to load course',
        'courses.whatYouWillLearn': 'What you will learn',
        'courses.courseQuiz': 'Course quiz',
        'courses.invalidVideoConfiguration': 'Invalid video configuration',
        'courses.lessons': 'Lessons',
        'courses.quiz': 'Quiz',
        'courses.takeQuiz': 'Take Quiz',
        'courses.quizTitle': 'Course Quiz',
        'courses.questionOf': 'Question',
        'courses.of': 'of',
        'courses.passMark': 'Pass mark',
        'courses.progress': 'Progress',
        'courses.answered': 'answered',
        'courses.typeAnswer': 'Type your answer...',
        'courses.pleaseAnswerAll': 'Please answer all questions before submitting.',
        'courses.checkingAnswers': 'Checking answers…',
        'courses.submitting': 'Submitting…',
        'courses.congratulations': 'Congratulations! 🎉',
        'courses.quizSubmitted': 'Quiz submitted',
        'courses.youScored': 'You scored',
        'courses.certificateGenerated': 'Your certificate has been generated! 🎓',
        'courses.viewCertificates': 'You can view it in your Certificates tab.',
        'courses.earnedPoints': 'You earned',
        'courses.points': 'points',
        'courses.unlockedBadge': 'and unlocked the "Quiz Master" badge!',
        'courses.loadingQuiz': 'Loading quiz…',
        'courses.previous': 'Previous',
        'courses.submit': 'Submit',
        'courses.next': 'Next',
        'courses.failedToSubmitQuiz': 'Failed to submit quiz',
        'courses.close': 'Close',
        'courses.statusNotStarted': 'Not started',
        'courses.statusInProgress': 'In progress',
        'courses.statusCompleted': 'Completed',
        'courses.detailIntro': 'Please complete the quiz to pass the course. You’ll find all relevant information in the video and presentation below.',
        'courses.completedShort': 'completed',
        'courses.level': 'Level',
        'courses.courseNumber': 'Course',
        'courses.learningPath': 'Learning Path',
        'courses.categoryDevelopment': 'Development',
        'courses.startQuiz': 'Start quiz',
        'courses.retakeQuiz': 'Retake quiz',
        'courses.viewAnswers': 'View answers',
        'courses.lastScore': 'Last quiz score',
        'courses.reviewPassed': 'Great job! You passed the quiz 🎉',
        'courses.reviewTryAgain': 'Quiz submitted review your answers',
        'courses.correctOutOf': 'correct answers',
        'courses.noAnswer': 'No answer given',
        'courses.prevCourse': 'Previous course',
        'courses.nextCourse': 'Next course',
        'courses.answerCorrect': 'Correct',
        'courses.answerIncorrect': 'Incorrect',
        'courses.yourAnswer': 'Your answer',
        'courses.correctAnswer': 'Correct answer',
        'courses.quizPassedLabel': 'Quiz Passed',
        'courses.quizFailedLabel': 'Quiz Failed',
        'courses.certificateReady': 'Your certificate is ready',
        'courses.certificateDescription': 'You have successfully completed this course. Generate your certificate to download and share it.',
        'courses.generateCertificate': 'Generate certificate',
        
        // Certificates
        'certificates.certificates': 'Certificates',
        'certificates.loading': 'Loading certificates…',
        'certificates.noCertificates': 'No certificates yet',
        'certificates.noCertificatesDesc': 'Once you complete all quizzes in a level, your certificates will appear here.',
        'certificates.issued': 'Issued',
        'certificates.type': 'Type',
        'certificates.download': 'Download',
        'certificates.downloadPDF': 'Download',
        'certificates.share': 'Share',
        'certificates.certificate': 'Certificate',
        'certificates.ofCompletion': 'Of Completion',
        'certificates.thisIsToCertify': 'This is to certify that',
        'certificates.hasSuccessfullyCompleted': 'Has successfully completed the course titled',
        'certificates.studentName': 'Student Name',
        'certificates.verification.title': 'Airies Certificate Verification',
        'certificates.verification.verified': 'Verified',
        'certificates.verification.notFound': 'Certificate not found',
        'certificates.verification.notFoundDesc': 'This verification link is invalid or the certificate has been revoked.',
        'certificates.verification.error': 'Verification Error',
        'certificates.verification.errorDesc': "We couldn't verify this certificate right now. Please try again later.",
        'certificates.verification.loading': 'Verifying certificate…',
        'certificates.verification.certificateCode': 'Certificate Code',
        'certificates.verification.issuedOn': 'Issued on',
        'certificates.verification.shareOnLinkedIn': 'Share on LinkedIn',
        'certificates.verification.downloadPDF': 'Download',
        'certificates.verification.authenticityNote': 'This page confirms the authenticity of an Airies certificate.',
        'certificates.courseTitle': 'Course Title',
        'certificates.airiesTeam': 'www.airies.app',
        'certificates.shareOnLinkedIn': 'Share on LinkedIn',
        'certificates.failedToLoad': 'Failed to load certificates',
        'certificates.failedToDownload': 'Failed to download certificate',
        'certificates.failedToShare': 'Failed to get share link',
        'certificates.linkedinInstructionsTitle': 'How to add this to your LinkedIn profile',
        'certificates.linkedinNameLabel': 'Name',
        'certificates.linkedinOrgLabel': 'Issuing organization',
        'certificates.linkedinIssueDateLabel': 'Issue date',
        'certificates.linkedinExpirationLabel': 'Expiration',
        'certificates.linkedinCredentialIdLabel': 'Credential ID',
        'certificates.linkedinCredentialUrlLabel': 'Credential URL',
        'certificates.verificationUrl': 'Verification URL',
        'certificates.linkedinNoExpiration': 'No expiration',
        'certificates.linkedinShareSummary': "I've completed: {name} on AIRIES!",
        'certificates.level1Description': 'Basic AI literacy, confidence, and applied beginner skills.',
        'certificates.level2Description': 'Mastery of AI tools for studying, writing, research, productivity, and early career development.',
        'certificates.level3Description': 'The learner can use AI at work, automate workflows, improve communication, and build real productivity systems.',
        'certificates.level4Description': 'The learner can design AI systems, lead teams with automation, and build scalable AI-driven operations.',
        'certificates.preparingDownload': 'Preparing download...',
        'certificates.loadingBackground': 'Loading background image...',
        'certificates.convertingBackground': 'Converting background image...',
        'certificates.generatingCertificate': 'Generating certificate...',
        'certificates.loadingImages': 'Loading images...',
        'certificates.finalizing': 'Finalizing...',
        'certificates.rendering': 'Rendering certificate...',
        'certificates.downloading': 'Downloading...',
        'certificates.issuingOrganization': 'Airies LLC',
        
        // Video Players
        'video.loading': 'Loading video...',
        'video.failedToLoadLibrary': 'Failed to load video player library.',
        'video.browserNotSupported': 'Your browser does not support DRM-protected video playback.',
        'video.errorOccurred': 'An error occurred while playing the video.',
        'video.licenseFailed': 'Failed to obtain video license. Please check your authentication.',
        'video.loadFailed': 'Failed to load video. Please check your internet connection.',
        'video.drmUnavailable': 'DRM system is not available in your browser.',
        'video.tryRefreshing': 'Please try refreshing the page or contact support if the issue persists.',
        'video.invalidPlaybackId': 'Invalid playback ID',
        'video.invalidVimeoUrl': 'Invalid Vimeo URL. Please provide a valid Vimeo video URL or ID.',
        'video.failedToInitialize': 'Failed to initialize Vimeo player.',
        'video.checkVimeoUrl': 'Please check the Vimeo URL and try again.',

        // Tools Explained
        'toolsExplained.title': 'Tools Explained',
        'toolsExplained.empty': 'No tools explained yet.',
        'toolsExplained.watch': 'Watch',
        'toolsExplained.moreToCome': 'More to come',
        
        // Share
        'share.shareTool': 'Share Tool',
        'share.linkCopied': 'Link copied to clipboard!',
        'share.linkCopiedForInstagram': 'Link copied! Open Instagram and paste to share.',
        'share.failedToCopy': 'Failed to copy link',
        
        // Community
        'community.community': 'Community',
        'community.stats': 'Community Stats',
        'community.totalPosts': 'Total Posts',
        'community.totalComments': 'Total Comments',
        'community.activeUsers': 'Active Users',
        'community.rooms': 'Rooms',
        'community.posts': 'Posts',
        'community.loadingPosts': 'Loading posts…',
        'community.noPosts': 'No posts found',
        'community.noPostsSubtitle': 'Be the first to post in this room!',
        'community.createPost': 'Create Post',
        'community.postTitle': 'Post Title',
        'community.postContent': 'Post Content',
        'community.postPlaceholder': 'What\'s on your mind?',
        'community.submitPost': 'Submit Post',
        'community.reply': 'Reply',
        'community.replies': 'Replies',
        'community.loadingReplies': 'Loading replies…',
        'community.writeReply': 'Write a reply...',
        'community.submitReply': 'Submit Reply',
        'community.like': 'Like',
        'community.unlike': 'Unlike',
        'community.comment': 'Comment',
        'community.comments': 'Comments',
        'community.viewReplies': 'View Replies',
        'community.hideReplies': 'Hide Replies',
        'community.failedToLoad': 'Failed to load community data',
        'community.failedToPost': 'Failed to create post',
        'community.failedToReply': 'Failed to create reply',
        'community.failedToUploadAttachment': 'Failed to upload attachment',
        'community.errorMissingContent': 'Please add content for your post.',
        'community.errorSelectRoom': 'Please select a room first.',
        'community.errorRoomNotFound': 'Room not found',
        'community.searchPlaceholder': 'Search by name',
        'community.activeRooms': 'Active Rooms',
        'community.noRepliesYet': 'No replies yet. Be the first to reply!',
        'community.selectRoomLabel': 'Select Room',
        'community.selectRoomPlaceholder': 'Choose a room',
        'community.linkLabel': 'Link (optional)',
        'community.linkTitleLabel': 'Link Title',
        'community.linkImageUrlLabel': 'Link Image URL',
        'community.linkDescriptionLabel': 'Link Description',
        'community.linkDescriptionPlaceholder': 'Short summary shown in the preview',
        'community.attachmentsLabel': 'Attachments (images / documents)',
        'community.uploadingAttachment': 'Uploading…',
        'community.submittingQuestion': 'Posting...',
        
        // Rewards
        'rewards.rewards': 'Rewards',
        'rewards.summary': 'Rewards Summary',
        'rewards.history': 'Rewards History',
        'rewards.prompts': 'Unlocked Prompts',
        'rewards.howToUnlockPrompts': 'Unlock prompts by completing levels. Each level you pass unlocks a new prompt.',
        'rewards.totalPoints': 'Total Points',
        'rewards.availablePoints': 'Available Points',
        'rewards.redeemedPoints': 'Redeemed Points',
        'rewards.recentActivity': 'Recent Activity',
        'rewards.loadingHistory': 'Loading history…',
        'rewards.noHistory': 'No reward history yet',
        'rewards.startLearning': 'Start learning to earn rewards!',
        'rewards.points': 'points',
        'rewards.earned': 'Earned',
        'rewards.redeemed': 'Redeemed',
        'rewards.failedToLoad': 'Failed to load rewards data',
        'rewards.quizScoreDescription': 'Scored {score}% on quiz',
        'rewards.tierPlatinum': 'Platinum',
        'rewards.tierGold': 'Gold',
        'rewards.tierSilver': 'Silver',
        'rewards.learnerSuffix': 'Learner',
        'rewards.bannerPlatinumAchieved': 'Platinum Learner Achieved!',
        'rewards.bannerToPlatinum': 'Level up to Platinum Learner',
        'rewards.bannerToGold': 'Level up to Gold Learner',
        'rewards.currentPoints': 'Current Points',
        'rewards.pointsUntilNext': '{points} points until next level',
        'rewards.maxLevelReached': 'Maximum level reached!',
        'rewards.lifetimePoints': 'Lifetime Points',
        'rewards.unlockedPrompts': 'Unlocked Prompts',
        'rewards.singlePromptUnlocked': '1 prompt unlocked',
        'rewards.multiplePromptsUnlocked': '{count} prompts unlocked',
        'rewards.unlockedAt': 'Unlocked {timeAgo}',
        'rewards.noPromptsTitle': 'No prompts unlocked yet',
        'rewards.noPromptsSubtitle': 'Complete all courses in a level to unlock premium prompts',
        'rewards.copyPrompt': 'Copy Prompt',
        'rewards.copyPromptSuccess': 'Prompt copied to clipboard',
        'rewards.copyPromptError': 'Failed to copy prompt',
        
        // Events
        'events.events': 'Events',
        'events.loading': 'Loading events…',
        'events.noEvents': 'No events found',
        'events.upcoming': 'Upcoming',
        'events.past': 'Past',
        'events.saveSeat': 'Save seat',
        'events.viewDetails': 'View Details',
        'events.failedToLoad': 'Failed to load events',
        'events.subtitle': 'Explore upcoming and past AI events, webinars and summits.',
        'events.upcomingTitle': 'Upcoming events & updates',
        'events.viewAllUpdates': 'View all Updates',
        'events.eventLabel': 'Event',
        'events.noEventsFound': 'No Events Found',
        'events.noEventsSubtitle': 'Upcoming events & webinars from your university will appear here.',
        
        // Notifications
        'notifications.notifications': 'Notifications',
        'notifications.loading': 'Loading notifications…',
        'notifications.noNotifications': 'No notifications',
        'notifications.markAllRead': 'Mark all as read',
        'notifications.failedToLoad': 'Failed to load notifications',
        'notifications.allCaughtUp': 'You\'re all caught up!',
        'notifications.viewAll': 'View all notifications',
        'notifications.markAllReadShort': 'Mark all read',
        
        // Pricing
        'pricing.comparePlans': 'Compare Plans',
        'pricing.compareBestPlans': 'Compare our best plans all together',
        'pricing.plansAvailable': 'plans available',
        'pricing.plan': 'plan',
        'pricing.features': 'Features',
        'pricing.joinWaitlist': 'Join Waitlist',
        'pricing.failedToLoad': 'Failed to load pricing plans',
        'pricing.label': 'Pricing',
        'pricing.title': 'Flexible Plans for Every Need',
        'pricing.description': 'Choose the plan that fits your AI journey start free, grow or scale with our best plans.',
        'pricing.perMonth': 'per month',
        'pricing.perYear': 'per year',
        'pricing.billedMonthly': 'Billed monthly',
        'pricing.billedAnnually': 'Billed annually',
        'pricing.exclVat': 'excl. VAT',
        'pricing.discountPercent': '% OFF',
        'pricing.compareAllPlans': 'Compare all plans',
        'pricing.notOpenForIndividuals': 'Note: The platform is not yet open for individual users. Please contact us for team or institutional access.',
        'pricing.mostPopular': 'Most Popular',
        'pricing.planFallback': 'Plan',
        'pricing.featuresComingSoon': 'Feature details coming soon.',
        'pricing.included': 'Included',
        'pricing.notIncluded': 'Not included',
        'pricing.continueToCheckout': 'Continue to Checkout',
        'pricing.getFullAccess': 'Get Full Access',
        'pricing.getCorporateAccess': 'Get Corporate Access',
        'pricing.haveLicenseKey': 'Do you have a license key?',
        
        // Contact
        'contact.title': 'Contact',
        'contact.subtitle': 'Tell us a bit about yourself and how we can help – our team will get back to you soon.',
        'contact.firstNameLabel': 'First name',
        'contact.firstNamePlaceholder': 'First name',
        'contact.lastNameLabel': 'Last name',
        'contact.lastNamePlaceholder': 'Last name',
        'contact.emailLabel': 'Email',
        'contact.emailPlaceholder': 'you@company.com',
        'contact.phoneLabel': 'Phone',
        'contact.phonePlaceholder': 'Enter your phone number',
        'contact.inquiryCategoryLabel': 'Inquiry category',
        'contact.inquiryCategoryHelp': 'The form updates to show relevant fields for your selection.',
        'contact.companyNameLabel': 'Company name',
        'contact.companyNamePlaceholder': 'Your organisation',
        'contact.websiteLabel': 'Website / domain',
        'contact.websitePlaceholder': 'https://example.com',
        'contact.productNameLabel': 'Product name',
        'contact.productNamePlaceholder': 'Your product name',
        'contact.productUrlLabel': 'Product URL',
        'contact.toolCategoryLabel': 'Category',
        'contact.toolCategoryPlaceholder': 'Select a category',
        'contact.toolCategory.nlp': 'NLP',
        'contact.toolCategory.vision': 'Vision',
        'contact.toolCategory.agents': 'Agents',
        'contact.toolCategory.analytics': 'Analytics',
        'contact.toolCategory.robotics': 'Robotics',
        'contact.monetisationModelLabel': 'Monetisation model',
        'contact.monetisationModelPlaceholder': 'Select a model',
        'contact.monetisation.free': 'Free',
        'contact.monetisation.freemium': 'Freemium',
        'contact.monetisation.subscription': 'Subscription',
        'contact.monetisation.oneTime': 'One time',
        'contact.valuePropositionLabel': 'Value proposition (max 240 chars)',
        'contact.valuePropositionPlaceholder': 'Why your product matters',
        'contact.commercialNumberLabel': 'Commercial register / HR number',
        'contact.commercialNumberPlaceholder': 'Your business number',
        'contact.industryLabel': 'Industry',
        'contact.industryPlaceholder': 'Select industry',
        'contact.industry.software': 'Software',
        'contact.industry.hardware': 'Hardware',
        'contact.industry.media': 'Media',
        'contact.industry.education': 'Education',
        'contact.industry.finance': 'Finance',
        'contact.industry.other': 'Other',
        'contact.productModelLabel': 'Product line / model',
        'contact.productModelPlaceholder': 'Model name / SKU',
        'contact.datasheetUrlLabel': 'Datasheet / spec URL',
        'contact.certificationsLabel': 'Certifications',
        'contact.certificationsPlaceholder': 'Select certificate',
        'contact.integrationInterestLabel': 'Integration interest',
        'contact.integrationInterestPlaceholder': 'Describe integration requirements & platform interests',
        'contact.objectiveLabel': 'Objective',
        'contact.objectivePlaceholder': 'Select objective',
        'contact.objective.branding': 'Branding',
        'contact.objective.leads': 'Lead generation',
        'contact.objective.launch': 'Launch',
        'contact.objective.event': 'Event',
        'contact.budgetRangeLabel': 'Budget range',
        'contact.budgetRangePlaceholder': 'Select budget range',
        'contact.budget.free': 'Free',
        'contact.budget.1to5k': '$1k–$5k',
        'contact.budget.5to20k': '$5k–$20k',
        'contact.budget.20kPlus': '$20k+',
        'contact.activationGoalsLabel': 'Activation goals',
        'contact.activationGoalsPlaceholder': 'Brand awareness, demos & lead capture',
        'contact.publicationLabel': 'Outlet / publication',
        'contact.publicationPlaceholder': 'Publication or media outlet',
        'contact.deadlineLabel': 'Deadline',
        'contact.coverageTypeLabel': 'Coverage type',
        'contact.coverageTypePlaceholder': 'Select type',
        'contact.coverageType.feature': 'Feature',
        'contact.coverageType.news': 'News',
        'contact.coverageType.interview': 'Interview',
        'contact.coverageType.review': 'Review',
        'contact.eventNameLabel': 'Event name & organiser',
        'contact.eventNamePlaceholder': 'Event name & organiser',
        'contact.eventStartDateLabel': 'Start date',
        'contact.eventEndDateLabel': 'End date',
        'contact.eventVenueLabel': 'Venue information',
        'contact.eventVenuePlaceholder': 'Venue address or online',
        'contact.countryCodeLabel': 'Country code',
        'contact.submitButton': 'Submit inquiry',
        'contact.eventUrlLabel': 'Event & ticketing URL',
        'contact.eventCategoryLabel': 'Category',
        'contact.eventCategoryPlaceholder': 'Select category',
        'contact.eventCategory.conference': 'Conference',
        'contact.eventCategory.workshop': 'Workshop',
        'contact.eventCategory.meetup': 'Meetup',
        'contact.eventDescriptionLabel': 'Description',
        'contact.eventDescriptionPlaceholder': 'Short event description',
        'contact.issueTypeLabel': 'Issue type',
        'contact.issueTypePlaceholder': 'Select an issue',
        'contact.issueType.ticketing': 'Ticketing',
        'contact.issueType.access': 'Access / login',
        'contact.issueType.accessibility': 'Accessibility',
        'contact.detailsLabel': 'Details',
        'contact.detailsPlaceholder': 'Short description of the problem',
        'contact.otherMessageLabel': 'How can we help?',
        'contact.otherMessagePlaceholder': 'Share any details about your enquiry',
        'contact.messageLabel': 'Message',
        'contact.messagePlaceholder': 'Please provide details',
        'contact.errorRequiredFields': 'Please fill in all required fields.',
        'contact.invalidEmail': 'Please enter a valid email address (e.g. name@company.com)',
        'contact.invalidPhone': 'Please enter a valid phone number (at least 6 digits)',
        'contact.successMessage': 'Thank you! Your enquiry has been submitted.',
        'contact.genericError': 'Something went wrong while submitting the form.',
        'contact.submitting': 'Submitting…',
        'contact.submitEnquiry': 'Submit enquiry',
        'contact.prefilledTopicLabel': 'Inquiry topic',
        'contact.prefilledTopics.affiliate-program': 'Affiliate Program',
        'contact.prefilledTopics.student-ambassador': 'Student Ambassador Program',
        'contact.prefilledMessages.affiliate-program': 'I would like to apply for the Airies Affiliate Program. Please share the next steps.',
        'contact.prefilledMessages.student-ambassador': 'I would like to apply for the Airies Student Ambassador Program. Please share the next steps.',
        'contact.tabs.partnerships': 'Partnerships (B2B)',
        'contact.tabs.submitTool': 'Submit your AI tool',
        'contact.tabs.university': 'Universities and Co-orporate',
        'contact.tabs.robotics': 'Robotics & Hardware',
        'contact.tabs.ads': 'Advertising & Sponsorships',
        'contact.tabs.press': 'Press & Media',
        'contact.tabs.support': 'Support / Private inquiry',
        'contact.tabs.events': 'Events (sponsor / speaker / list)',
        'contact.tabs.other': 'Other',
        'contact.common.other': 'Other',
        'support.buttonLabel': 'Support',
        'support.title': 'Get Support',
        'support.nameLabel': 'Name',
        'support.namePlaceholder': 'Your full name',
        'support.emailLabel': 'Email',
        'support.emailPlaceholder': 'your@email.com',
        'support.subjectLabel': 'Subject',
        'support.subjectPlaceholder': 'Brief description of your issue',
        'support.messageLabel': 'Message',
        'support.messagePlaceholder': 'Please provide details about your issue...',
        'support.categoryLabel': 'Category',
        'support.categoryPlaceholder': 'Select a category',
        'support.category.general': 'General question',
        'support.category.bug': 'Bug',
        'support.category.account': 'Account issue',
        'support.category.other': 'Other',
        'support.submitButton': 'Send to Support',
        'support.submitting': 'Sending...',
        'support.successTitle': 'Thanks!',
        'support.successMessage': 'Our support team will get back to you soon.',
        'support.errorMessage': 'Something went wrong, please try again.',
        'support.close': 'Close',
        'support.required': 'This field is required',
        'contact.universityNameLabel': 'University name',
        'contact.universityNamePlaceholder': 'e.g. Oxford University',
        'contact.universityDomainLabel': 'University domain',
        'contact.universityDomainPlaceholder': 'e.g. oxford.edu',
        'contact.universityAdminNameLabel': 'Admin name',
        'contact.universityAdminNamePlaceholder': 'Administrator full name',
        'contact.universityAdminEmailLabel': 'Admin email',
        'contact.universityAdminEmailPlaceholder': 'admin@university.edu',
        'contact.universityAdminPhoneLabel': 'Admin phone',
        'contact.universityAdminPhonePlaceholder': '+44 9900 12345',
        'contact.universitySizeLabel': 'University size',
        'contact.universitySizePlaceholder': 'e.g. 1000–5000 Students',
        'contact.universityCountryLabel': 'Country',
        'contact.universityCountryPlaceholder': 'e.g. UK',
        'contact.universityNotesLabel': 'Additional notes',
        'contact.universityNotesPlaceholder': 'Any additional information about your university',
        
        // Universities
        'universities.title': 'Partner Universities',
        'universities.subtitle': 'We offer free access to leading universities. If your university or faculty is not on the list or you have trouble logging in, please apply by clicking on the button. You can check your access by pressing "Get started" in the header, then "Sign in with SSO" with your university email address',
        'universities.applyButton': 'Apply for my university',
        'universities.loading': 'Loading universities…',
        'universities.failedToLoad': 'Failed to load universities',
        'universities.emptyTitle': 'No universities found',
        'universities.emptySubtitle': 'Once universities are connected to AIRIES, they will appear here.',
        'universities.marqueeHeading': 'Free University Access',
        'universities.marqueeTitle': 'Trusted by students and teams at universities across Europe',
        'universities.viewAll': 'View all universities',
        'universities.searchPlaceholder': 'Search universities...',
        'universities.noResults': 'No universities found',
        'universities.noResultsDesc': 'Try searching for something else or clear your search.',
        'universities.clearSearch': 'Clear search',
        'universities.showingResults': 'Showing',
        'universities.result': 'result',
        'universities.results': 'results',
        'universities.for': 'for',
        'universities.university': 'university',
        'universities.universities': 'universities',
        
        // Cookies
        'cookies.title': 'We value your privacy',
        'cookies.summary':
            'We use essential cookies to make this site work and optional analytics and marketing cookies to understand usage and improve your experience. You can accept all cookies or reject non-essential cookies now and change your choice later in our Cookie Policy.',
        'cookies.acceptAll': 'Accept all cookies',
        'cookies.reject': 'Reject non-essential',
        'cookies.learnMore': 'Cookie Policy',
        
        // Newsletter popup
        'newsletterPopup.title': 'Get 10% off your Airies subscription',
        'newsletterPopup.description': 'Sign up for our AI newsletter and receive practical tips, new tools, and learning resources every week.',
        'newsletterPopup.cta': 'Claim my 10% discount',
        'newsletterPopup.skip': 'Maybe later',
        'newsletterPopup.subscribed': 'Successfully subscribed!',
        'couponPopup.text': 'Only this week - save 20% on any subscription with Coupon Code Learn-AI-NOW20',
        'couponPopup.offerLine': 'Only this week - save 20% on any subscription',
        'couponPopup.codeLabel': 'Coupon code',
        'couponPopup.expiresIn24Hours': 'Expires in 24 hours',
        'couponPopup.getFullAccessCta': 'Get full access with 20% off',
        'couponPopup.expiresInDays': 'Expires in 7 days',
        'couponPopup.expiresLabel': 'Coupon Expires',
        'couponPopup.expiresDate': '09/03',
        'couponPopup.discountTitle': '20% OFF',
        'couponPopup.copy': 'Copy code',
        'couponPopup.copySuccess': 'Coupon code copied',
        'couponPopup.copyError': 'Failed to copy coupon code',
        // Footer
        'footer.stayUpdated': 'Stay Updated on AI tool launches & tutorials.',
        'footer.getEarlyAccess': 'Get early access to AI events, trending tools, and insider tips your shortcut to mastering the future.',
        'footer.enterEmail': 'Enter your email',
        'footer.invalidEmail': 'Please enter a valid email address (e.g. name@domain.com)',
        'footer.getStarted': 'Get started',
        'footer.description': 'Search tools, watch structured lessons, follow learning paths & stay updated with latest tools, news and events.',
        'footer.affiliatePromoEyebrow': 'Partner with Airies',
        'footer.affiliatePromoTitle': 'Launch your recurring income with the Airies Affiliate Program.',
        'footer.affiliatePromoDescription': 'Explore the affiliate and student ambassador program built for creators, communities, and campuses promoting practical AI education.',
        'footer.affiliatePromoCta': 'View Affiliate Page',
        'footer.company': 'Company',
        'footer.resources': 'Resources',
        'footer.solutions': 'Solutions',
        'footer.support': 'Support',
        'footer.legal': 'Legal',
        'footer.allRightsReserved': 'All Rights Reserved',
        'footer.allServicesOnline': 'All services are online',
        'footer.privacyPolicy': 'Privacy Policy',
        'footer.terms': 'Terms',

        // Affiliate page
        'affiliate.hero.eyebrow': 'Affiliate program',
        'affiliate.hero.title': 'Earn recurring income promoting AI education',
        'affiliate.hero.titleLineOne': 'Earn recurring income',
        'affiliate.hero.titleLineTwo': 'by promoting',
        'affiliate.hero.titleHighlight': 'practical AI education.',
        'affiliate.hero.subtitle': 'Join the Airies Affiliate Program and earn monthly recurring commission by helping people learn practical AI skills.',
        'affiliate.hero.highlights.firstPayment': '40% commission on the first payment',
        'affiliate.hero.highlights.recurring': '20% recurring monthly commission',
        'affiliate.hero.highlights.referralCode': 'Personal referral code',
        'affiliate.hero.highlights.fastGrowing': 'Promote a fast-growing AI learning platform',
        'affiliate.hero.ctaAffiliate': 'Become an Affiliate',
        'affiliate.hero.ctaAmbassador': 'Become a Student Ambassador',
        'affiliate.hero.trustLine': '10,000+ learners already using Airies',
        'affiliate.hero.cardLabel': 'Recurring revenue model',
        'affiliate.hero.cardRecurringLabel': 'Monthly recurring commission',
        'affiliate.hero.cardRecurringValue': '20%',
        'affiliate.hero.cardRecurringDescription': 'recurring monthly commission',
        'affiliate.hero.cardFirstPaymentLabel': 'First payment',
        'affiliate.hero.cardFirstPaymentValue': '40%',
        'affiliate.hero.cardFirstPaymentDescription': 'commission on the first payment',
        'affiliate.hero.cardPlatformLabel': 'Subscription price',
        'affiliate.hero.cardPlatformValue': '€ 25.90',
        'affiliate.hero.cardPlatformDescription': 'Clear price makes it easy to explain',
        'affiliate.hero.imageAlt': 'Happy affiliate reviewing results on a laptop and phone',
        'affiliate.howItWorks.eyebrow': 'How it works',
        'affiliate.howItWorks.title': 'How the Airies Affiliate Program works',
        'affiliate.howItWorks.subtitle': 'Practical, structured AI learning designed for curious minds, modern professionals, and forward-thinking teams.',
        'affiliate.howItWorks.steps.join.title': 'Join the program',
        'affiliate.howItWorks.steps.join.description': 'Apply to become an affiliate or student ambassador.',
        'affiliate.howItWorks.steps.code.title': 'Get your referral code',
        'affiliate.howItWorks.steps.code.description': 'You receive a personal code to share with your audience.',
        'affiliate.howItWorks.steps.earn.title': 'Earn recurring commission',
        'affiliate.howItWorks.steps.earn.description': 'Every paying subscriber you refer earns you monthly income.',
        'affiliate.earnings.eyebrow': 'Earnings potential',
        'affiliate.earnings.title': 'Your recurring income potential',
        'affiliate.earnings.subtitle': 'Airies subscriptions are EUR 25.90 per month and affiliates earn 20% recurring commission.',
        'affiliate.earnings.planPriceLabel': 'Subscription price',
        'affiliate.earnings.planPriceValue': 'EUR 25.90 / month',
        'affiliate.earnings.commissionLabel': 'Affiliate recurring commission',
        'affiliate.earnings.commissionValue': '20% / month',
        'affiliate.earnings.examples.hundred.audience': '100 subscribers',
        'affiliate.earnings.examples.hundred.revenue': '100 × EUR 25.90 = EUR 2,590 monthly revenue',
        'affiliate.earnings.examples.hundred.payout': 'EUR 518 / month',
        'affiliate.earnings.examples.hundred.payoutLabel': 'Your recurring commission',
        'affiliate.earnings.examples.thousand.audience': '1,000 subscribers',
        'affiliate.earnings.examples.thousand.revenue': '1,000 × EUR 25.90 = EUR 25,900 monthly revenue',
        'affiliate.earnings.examples.thousand.payout': 'EUR 5,180 / month',
        'affiliate.earnings.examples.thousand.payoutLabel': 'Your recurring commission',
        'affiliate.earnings.examples.tenThousand.audience': '10,000 subscribers',
        'affiliate.earnings.examples.tenThousand.revenue': '10,000 × EUR 25.90 = EUR 259,000 monthly revenue',
        'affiliate.earnings.examples.tenThousand.payout': 'EUR 51,800 / month',
        'affiliate.earnings.examples.tenThousand.payoutLabel': 'Your recurring commission',
        'affiliate.earnings.note': 'Top affiliates can grow their referrals over time and build substantial monthly recurring income.',
        'affiliate.audience.eyebrow': 'Who should join',
        'affiliate.audience.title': 'Who the Airies Affiliate Program is for',
        'affiliate.audience.items.contentCreators': 'AI content creators',
        'affiliate.audience.items.videoCreators': 'YouTubers and TikTok creators',
        'affiliate.audience.items.linkedinCreators': 'LinkedIn creators',
        'affiliate.audience.items.newsletterOwners': 'Newsletter owners',
        'affiliate.audience.items.studentAmbassadors': 'Student ambassadors',
        'affiliate.audience.items.universityClubs': 'University clubs',
        'affiliate.audience.items.consultants': 'Consultants and educators',
        'affiliate.audience.items.communities': 'Communities interested in AI',
        'affiliate.studentAmbassador.eyebrow': 'Campus ambassador track',
        'affiliate.studentAmbassador.title': 'Students can represent Airies on campus',
        'affiliate.studentAmbassador.description': 'Students can also apply to represent Airies on campus and promote AI literacy.',
        'affiliate.studentAmbassador.benefits.freeAccess': 'Free access to Airies',
        'affiliate.studentAmbassador.benefits.personalCode': 'Personal referral code',
        'affiliate.studentAmbassador.benefits.commission': 'Commission on referrals',
        'affiliate.studentAmbassador.benefits.recognition': 'Ambassador recognition for CV and LinkedIn',
        'affiliate.finalCta.eyebrow': 'Start now',
        'affiliate.finalCta.title': 'Start earning recurring income with Airies',
        'affiliate.finalCta.subtitle': 'Apply to join as an affiliate or student ambassador and turn your audience, community, or campus reach into monthly recurring income.',
        'affiliate.finalCta.ctaAffiliate': 'Apply to Become an Affiliate',
        'affiliate.finalCta.ctaAmbassador': 'Become a Student Ambassador',

        // Auth
        'auth.welcomeBack': 'Welcome Back 😃',
        'auth.weMissedYou': 'We missed you, please enter your details to sign in',
        'auth.signInToAccount': 'Sign in to your account',
        'auth.signInWithSSO': 'Sign in with SSO',
        'auth.signInWithUniversity': 'Sign in with your university email',
        'auth.resetPassword': 'Reset password & sign in',
        'auth.backToSignIn': 'Back to sign in',
        'auth.signInToAdmin': 'Sign in to Admin',
        'auth.signInToAdminDashboard': 'Sign in to access the admin dashboard',
        'auth.waitlistTitle': 'Join the Waitlist 🚀',
        'auth.waitlistDescription': 'Be the first to know when we launch. Get early access to AIRiES.',
        'auth.waitlistSuccessTitle': "You're on the waitlist! 🎉",
        'auth.waitlistAlreadyOnListTitle': "You're already on the list!",
        'auth.waitlistSuccessDescription': "Thank you for joining! We'll notify you as soon as we launch.",
        'auth.waitlistAlreadyOnListDescription': "We already have your email. We'll notify you as soon as we launch!",
        'auth.alreadyHaveSSO': 'Already have SSO access?',
        'auth.signInWithUniversityCta': 'Sign in with your university email',
        'auth.backToHome': 'Back to Home',
        'auth.fullNameLabel': 'Full Name',
        'auth.fullNamePlaceholder': 'John Doe',
        'auth.emailLabel': 'Email Address',
        'auth.emailPlaceholder': 'you@example.com',
        'auth.invalidEmail': 'Please enter a valid email address.',
        'auth.fullNameRequired': 'Please enter your full name.',
        'auth.genericError': 'Something went wrong, please try again.',
        'auth.joinWaitlist': 'Join the Waitlist',
        'auth.or': 'Or',
        'auth.ssoAccessThroughUniversity': 'Already have access through your University or Company?',
        'auth.corporateLoginTitle': 'Corporate Login',
        'auth.corporateLoginQuestion': 'Does your company have access to Airies?',
        'auth.corporateLoginNote': 'Only sign up here if you have a company email or email with a company domain that is registered with us.',
        'auth.studentLoginTitle': 'Student Login',
        'auth.studentLoginQuestion': 'Is your university eligible for free access to Airies',
        'auth.studentLoginNote': 'Only sign up here if you have a student/university email or email with a university domain that is registered with us.',
        'auth.studentLoginSeeList': 'see list',
        'auth.individualAccessTitle': 'Individual Access',
        'auth.individualAccessQuestion': 'Would you like to purchase individual access?',
        'auth.individualAccessDescription': 'and we keep you posted once this is possible',
        'auth.signUpHere': 'Sign up here',
        'auth.signInWithSSOCta': 'Sign in',
        'auth.universityEmailLabel': 'University or Company Email',
        'auth.universityEmailPlaceholder': 'you@university.edu or you@company.com',
        'auth.invalidUniversityEmail': 'Please enter a valid university or company email address.',
        'auth.otpAlreadyUsed': 'This OTP has already been used. Please request a new one.',
        'auth.otpExpired': 'Your OTP has expired. Please request a new one.',
        'auth.otpInvalid': 'The code you entered is incorrect.',
        'auth.passwordTooWeak': 'Password must be at least 8 characters and include letters and numbers.',
        'auth.emailDomainNotAllowed': 'This email domain is not allowed. Please use your university or company email. Or apply for access here',
        'auth.applyForAccess': 'Click here',
        'auth.accessRequestTitle': 'Apply for Access',
        'auth.accessRequestDescription': 'Fill out the form below to request access for your university or company.',
        'auth.accessRequestSuccess': 'Your access request has been submitted successfully. We will review your request and get back to you soon.',
        'auth.enterPassword': 'Please enter your password.',
        'auth.incorrectEmailOrPassword': 'Incorrect email or password.',
        'auth.licenseRequired': 'Please enter your license key.',
        'auth.licenseRedeemed': 'License redeemed. Continue your signup to activate access.',
        'auth.licenseValidCorporate': 'Corporate license detected.',
        'auth.licenseValidIndividual': 'Individual license detected.',
        'auth.completeSignup': 'Complete your signup',
        'auth.licenseValidatedIndividual': 'Your license has been validated. Complete your account setup below.',
        'auth.licenseClaimed': 'License activated successfully!',
        'auth.licenseSeatsRemaining': 'Seats remaining',
        'auth.redeemTitle': 'Redeem your license',
        'auth.redeemDescription': 'Enter your email and license key to continue your onboarding.',
        'auth.licenseKeyLabel': 'License key',
        'auth.licenseKeyPlaceholder': 'AIRIES-XXXX-XXXX-XXXX-XXXXX',
        'auth.redeemCta': 'Continue',
        'auth.redeemHelpText': 'Already signed up?',
        'auth.otpRequired': 'Please enter the OTP sent to your email.',
        'auth.fullNameRequiredForSignup': 'Please enter your full name.',
        'auth.passwordRequired': 'Please enter your password.',
        'auth.passwordRequiredForSignup': 'Please create a password.',
        'auth.newPasswordRequired': 'Please create a new password.',
        'auth.welcomeBackHeading': 'Welcome Back 😃',
        'auth.firstTimeHeading': 'Let’s get you set up 🎓',
        'auth.resetPasswordHeading': 'Reset your password',
        'auth.welcomeBackSubheading': 'We missed you, please enter your details to sign in',
        'auth.firstTimeSubheading': 'We’ve sent a 6-digit code to your university or company email',
        'auth.resetPasswordSubheading': 'Enter the OTP from your email to reset your password',
        'auth.ssoOtpSentStatus': 'Please check your university or company email for the 6-digit code.',
        'auth.ssoPasswordStepStatus': 'Welcome back! Please enter your password to continue.',
        'auth.ssoOtpResetStatus': 'We’ve sent an OTP to your email to reset your password.',
        'auth.passwordLabel': 'Password',
        'auth.rememberMe': 'Remember me',
        'auth.continue': 'Continue',
        'auth.forgotPassword': 'Forgot password?',
        'auth.passwordPlaceholder': 'Enter your password',
        'auth.showPassword': 'Show password',
        'auth.hidePassword': 'Hide password',
        'auth.otpLabel': 'One-Time Password',
        'auth.otpPlaceholder': '6-digit code',
        'auth.otpSpamHint': '(please check Spam)',
        'auth.fullNameSignupLabel': 'Full name',
        'auth.fullNameSignupPlaceholder': 'This name will be used for your certificates',
        'auth.createPasswordLabel': 'Create password',
        'auth.createPasswordPlaceholder': 'At least 8 characters',
        'auth.alternateEmailLabel': 'Alternate Email',
        'auth.alternateEmailPlaceholder': 'you@example.com',
        'auth.verifyAndCreateAccount': 'Verify & Create Account',
        'auth.resetOtpLabel': 'One-Time Password',
        'auth.resetOtpPlaceholder': '6-digit code',
        'auth.newPasswordLabel': 'New password',
        'auth.newPasswordPlaceholder': 'Create a strong password',
        'auth.continueWithGoogle': 'Continue with Google',
        'auth.orContinueWithEmail': '— or continue with email —',
        'auth.googleSignInFailed': 'Google sign-in failed. Please try again.',
        'auth.companyName': 'Airies Learning Platform',
        'auth.copyright': '© 2026 Airies GmbH',
        'auth.googleSignInSuccess': 'Signed in successfully!',
        'auth.completingSignIn': 'Completing sign-in...',
        'auth.redirectingToGetStarted': 'Redirecting to get started page...',
        'auth.redirectingToDashboard': 'Redirecting to dashboard...',
        'auth.resetPasswordAndSignIn': 'Reset password & sign in',
        'auth.backToSignInCta': 'Back to sign in',
        'auth.getStartedTitle': 'Get started',
        'auth.getStartedDescription': 'Sign in or create an account to access AIRIES.',
        'auth.discountCodeFullAccess': 'Do you have a discount code or would you like full access?',
        'auth.signUp': 'Sign up',
        'auth.signIn': 'Sign in',
        'auth.referralCodeLabel': 'Referral code',
        'auth.referralCodePlaceholder': 'Optional',
        'auth.referralCodeHelper': 'Have a referral link? It will be filled automatically.',
        'auth.referralCodeValid': 'Referral applied',
        'auth.referralCodeInvalid': 'Invalid referral code',
        'auth.validatingReferral': 'Validating...',
        'auth.applyReferral': 'Apply',
        'auth.createAccount': 'Create account',
        'auth.otpVerificationTitle': 'Verify your email',
        'auth.otpVerificationDescription': 'We sent a 6-digit code to your email. Please enter it below.',
        'auth.verifyEmail': 'Verify email',
        'auth.resendOtp': 'Resend code',
        'auth.otpSent': 'Code sent!',
        'auth.individual': 'Individual',
        'auth.studentCorporate': 'Student/Corporate',
        'auth.dontHaveAccount': "Don't have an account? ",
        'auth.alreadyHaveAccount': 'Already have an account? ',
        'auth.licenseInvalid': 'Invalid license',
        'auth.licenseSeatsExhausted': 'All seats for this license are already in use.',
        'auth.seatsExhaustedContactAdmin': 'All seats are currently occupied. Please contact your administrator.',
        'auth.licenseEntryTitle': 'Enter your email',
        'auth.licenseEntryDescription': 'Enter your email to continue with your license',
        'auth.skipLicense': 'Continue without license',
        'auth.sendResetCode': 'Send reset code',
        'auth.forgotPasswordDescription': 'Enter your email to receive a reset code',
        'auth.getStarted': 'Get started',
        
        // Referral
        'referral.title': 'Referral',
        'referral.description': 'Invite friends. When eligible referrals subscribe, you earn rewards.',
        'referral.yourReferralLink': 'Your referral link',
        'referral.copyLink': 'Copy link',
        'referral.share': 'Share',
        'referral.yourReferralCode': 'Your referral code',
        'referral.copyCode': 'Copy code',
        'referral.status': 'Status',
        'referral.successfulReferrals': 'Successful referrals',
        'referral.rewardStatus': 'Reward status',
        'referral.notEarned': 'Not earned',
        'referral.earned': 'Earned',
        'referral.linkCopied': 'Copied!',
        'referral.codeCopied': 'Copied!',
        'referral.failedToLoad': 'Failed to load referral information',
        'referral.shareTitle': 'Join AIRIES',
        'referral.shareDescription': 'Check out AIRIES and start your AI learning journey!',
        'referral.paidReferrals': 'Paid Referrals',
        'referral.noPaidReferrals': 'No paid referrals yet',
        'referral.rewardsPaused': 'Rewards paused',

        // Pricing
        'pricing.getStarted': 'Get started',
        'pricing.currentPlan': 'Current plan',
        'pricing.select': 'Select',
        'pricing.comingSoon': 'Coming soon',
        'pricing.paymentsNotEnabled': 'Payments not enabled yet',
        'pricing.contactUs': 'Contact Us',
        'pricing.startLearning': 'Start Learning',
        'pricing.contactForPricing': 'Contact us for pricing',
        'pricing.numberOfUsers': 'Number of users',
        'pricing.enterValidNumber': 'Please enter a number of 1 or more',
        
        // Upgrade Modal
        'upgradeModal.title': 'Upgrade to unlock all the levels',
        'upgradeModal.body': 'Join the waitlist to get early access when upgrades go live.',
        'upgradeModal.joinWaitlist': 'Join waitlist',
        'upgradeModal.notNow': 'Not now',
        
        // Waitlist
        'waitlist.title': 'Join the Waitlist',
        'waitlist.subtitle': 'Get early access when upgrades go live.',
        'waitlist.email': 'Email',
        'waitlist.emailPlaceholder': 'your@email.com',
        'waitlist.fullName': 'Full Name',
        'waitlist.fullNamePlaceholder': 'John Doe',
        'waitlist.note': 'Note / Interest',
        'waitlist.notePlaceholder': 'Tell us what interests you most...',
        'waitlist.optional': 'optional',
        'waitlist.submit': 'Join Waitlist',
        'waitlist.submitting': 'Submitting...',
        'waitlist.success': 'Successfully joined the waitlist!',
        'waitlist.successTitle': "You're on the waitlist!",
        'waitlist.successMessage': "We'll notify you when upgrades become available.",
        'waitlist.alreadyOnWaitlist': 'You are already on the waitlist!',
        'waitlist.error': 'Failed to join waitlist. Please try again.',
        'waitlist.backToCourses': 'Back to courses',
        
        // My Courses - Locked
        'myCourses.locked': 'Locked',
        'myCourses.unlock': 'Unlock',
        
        // Hero
        'hero.heading': 'Where AI Meets',
        'hero.headingHighlight': 'Ambition.',
        'hero.subheading': 'The AI learning platform built for everyone -individuals, students, and businesses. Accessible, affordable, and effective, even with no prior experience. Register now for a free trial!',
        'hero.startLearning': 'Start Learning',
        'hero.explorePlatform': 'Explore the platform',
        'hero.takeTour': 'Take a tour',
        'hero.requestDemo': 'Request demo',
        'hero.exploreDashboard': 'Explore our Learning Dashboard in 2 mins',
        'hero.productTour': 'Here\'s a product tour of how Airies works...',
        'hero.productTourTitle': 'HTML product tour of Airies Learning Dashboard',
        'hero.kickerNew': 'Get AI certified in days.',
        'hero.headingNew': 'Get AI certified in days',
        'hero.subheadingNew': 'Go from AI beginner to the AI expert at work with practical lessons built for the real world.',
        'hero.ctaPrimary': 'Start Free Trial',
        'hero.ctaSecondary': 'See how it works (2 min)',
        'hero.ctaPrimaryPlan': 'Start learning AI for free',
        'hero.ctaSecondaryPlan': 'Full platform access €25.90/month',
        'hero.priceNotePlan': 'Founding member pricing',
        'hero.ctaHelperLine': '5 free courses • no credit card',
        'hero.highlights.secureCheckout': '45+ courses\nyou actually need',
        'hero.highlights.moneyBack': '7-day money back\nguarantee',
        'hero.highlights.trustedLearners': 'Trusted by 10,000+\nlearners',
        'hero.tiles.essentialCourses.title': 'Essential Courses',
        'hero.tiles.essentialCourses.description': 'Start with 45+ carefully selected courses that focus on the AI skills people actually use for success.',
        'hero.tiles.moneyGuarantee.title': 'Money Guarantee',
        'hero.tiles.moneyGuarantee.description': 'Explore the platform risk-free and upgrade only when you are ready to go deeper.',
        'hero.tiles.trustedLearning.title': 'Trusted Learning',
        'hero.tiles.trustedLearning.description': 'Join 10,000+ learners building practical AI confidence with structured lessons and clear outcomes.',
        'hero.postCta.title': 'Start learning AI today',
        'hero.postCta.subtitle': '3 free courses included',
        'hero.postCta.button': 'Start Free',
        'hero.statsCompanies': '+500 companies have realized cost savings',
        'hero.statsUsers': 'Avg 1500 users online',
        'hero.statsGeographies': '200 geographies covered',
        'hero.proofStrip': 'Trusted by learners across Europe • Used by professionals & teams • AI courses from beginner to advanced',
        'hero.benefit1': 'Learn AI step by step — no tech background needed',
        'hero.benefit2': 'Hands-on tools, prompts & workflows',
        'hero.benefit3': 'Certificates for career & business use',

        // Official Partners
        'officialPartners.kicker': 'Official partners',
        'officialPartners.title': 'Airies is an official OpenAI, Google Cloud, and Miro partner.',
        'officialPartners.description': 'Explore partner-backed AI tools already available in the Airies tools marketplace.',
        'officialPartners.viewPartner': 'View partner tool',
        
        // Who This Is For
        'whoThisIsFor.title': 'Who this is for',
        'whoThisIsFor.students.title': 'Students & Beginners',
        'whoThisIsFor.students.desc': 'Learn AI from scratch — clearly and practically.',
        'whoThisIsFor.students.cta': 'See courses',
        'whoThisIsFor.professionals.title': 'Professionals',
        'whoThisIsFor.professionals.desc': 'Use AI to work faster, automate tasks and stay ahead.',
        'whoThisIsFor.professionals.cta': 'Explore skills',
        'whoThisIsFor.companies.title': 'Companies',
        'whoThisIsFor.companies.desc': 'Train teams safely and effectively with structured AI learning.',
        'whoThisIsFor.companies.cta': 'Corporate training',
        'whoThisIsForV2.title': 'Who this is for',
        'whoThisIsForV2.subtitle': 'Practical, structured AI learning designed for curious minds, modern professionals, and forward-thinking teams.',
        'whoThisIsForV2.cards.students.title': 'Students & Curious Beginners',
        'whoThisIsForV2.cards.students.desc': 'Start your AI journey from zero clearly, practically, and without overwhelm. Learn how AI works, how to prompt effectively, and how to apply tools in everyday work and studies.',
        'whoThisIsForV2.cards.professionals.title': 'Professionals & Builders',
        'whoThisIsForV2.cards.professionals.desc': 'Use AI to work faster, automate repetitive tasks, and unlock new creative and strategic capabilities. Master tools, workflows, and real-world applications used by modern teams.',
        'whoThisIsForV2.cards.teams.title': 'Teams & Organizations',
        'whoThisIsForV2.cards.teams.desc': 'Equip your team with structured AI training designed for safe, practical adoption across departments. Build internal AI capability without the chaos of scattered tools and tutorials.',
        'whoThisIsForV2.ready.title': 'Ready to Start Your AI Journey?',
        'whoThisIsForV2.ready.subtitle': 'Learn AI step-by-step with structured lessons, practical tools, and real-world workflows.',
        
        // Trust Strip
        'trustStrip.item1': 'No prior AI or coding experience needed',
        'trustStrip.item2': 'Learn at your own pace',
        'trustStrip.item3': 'Cancel anytime',
        'trustStrip.item4': 'Start free — no credit card required',
        
        // Final CTA
        'finalCta.heading': 'Ready to start?',
        'finalCta.cta': 'Start Free Trial',
        
        // Tools
        'tools.startLearning': 'Start learning',
        'tools.getStartedFree': 'Get started for free',
        'tools.recentlyAdded': 'Recently added',
        'tools.mostPopular': 'Most popular',
        'tools.highestRated': 'Highest rated',
        'tools.adjustFilters': 'Try adjusting your search or filters.',
        'tools.discoverTool': 'Discover tool',
        'tools.failedToLoad': 'Failed to load tools',
        'tools.trendingTitle': 'Trending AI Tools',
        'tools.trendingDescription': 'Explore the most popular AI tools and discover them to stay ahead in the AI revolution.',
        'tools.loading': 'Loading...',
        'tools.comingSoon': 'Coming soon',
        'tools.discoverToolWithArrow': 'Discover tool →',
        'toolsByCategory.title': 'Useful AI Tools by Category',
        'toolsByCategory.viewAll': 'View All tools →',
        'toolsByCategory.tabsAria': 'Tool categories',
        'tools.categoryLabel': 'Category',
        'tools.platformLabel': 'Platform',
        'tools.lastUpdated': 'Last updated',
        'tools.visitTool': 'Visit tool',
        'tools.courseNotAvailable': 'Course not available',

        // Upgrade Required (tool → course)
        'upgradeRequired.title': 'Upgrade required',
        'upgradeRequired.body': 'To learn this course, you need to subscribe to a plan.',
        'upgradeRequired.viewPlans': 'View plans',

        // Tool detail
        'toolDetail.sections.overview': 'Overview',
        'toolDetail.sections.pricing': 'Pricing & Plans',
        'toolDetail.sections.useCases': 'Use Cases',
        'toolDetail.sections.prosCons': 'Pros & Cons',
        'toolDetail.sections.reviews': 'Reviews',
        'toolDetail.sections.qa': 'Questions & Answers',
        'toolDetail.sections.courses': 'Courses',
        'toolDetail.sections.releases': 'Releases / Updates',
        'toolDetail.sections.alternatives': 'Alternatives',
        'toolDetail.sections.links': 'Links & Resources',
        'toolDetail.whyTeamsUseIt': 'Why teams use it',
        'toolDetail.pros': 'Pros',
        'toolDetail.cons': 'Cons',
        'toolDetail.addReview': 'Add a review',
        'toolDetail.averageRating': 'Average rating',
        'toolDetail.totalReviewsCount': 'Total reviews: {{count}}',
        'toolDetail.totalReviewsDescription': 'Total reviews till now',
        'toolDetail.helpful': 'Helpful',
        'toolDetail.reply': 'Reply',
        'toolDetail.notifyMe': 'Notify me',
        'toolDetail.featuresLabel': 'Features:',
        'toolDetail.altRatingSummary': 'Rating: {{rating}} ({{likes}} likes)',
        'toolDetail.vendorWebsite': 'Vendor website',
        'toolDetail.loginRequired': 'Please sign in to write a review.',
        'toolDetail.alreadyReviewed': 'You already reviewed this tool.',
        'toolDetail.reviewFailed': 'Failed to submit review.',
        'toolDetail.reviewSuccess': 'Review submitted successfully.',
        'toolDetail.totalReviews': 'reviews',
        'toolDetail.noReviews': 'No reviews yet',
        'toolDetail.noReviewsDescription': 'Be the first to share your experience.',
        'toolDetail.writeReview': 'Write a review',
        'toolDetail.yourRating': 'Your rating',
        'toolDetail.reviewTitle': 'Title',
        'toolDetail.reviewTitlePlaceholder': 'Optional – e.g. “Perfect for research workflows”',
        'toolDetail.reviewComment': 'Comment',
        'toolDetail.reviewCommentPlaceholder': 'Share how this tool helped your work, what you liked, and what could be better.',
        'toolDetail.submitting': 'Submitting...',
        'toolDetail.submitReview': 'Submit review',
        'categories.exploreTitle': 'Explore By Categories',
        'categories.description': 'Find tools and lessons by what you\'re trying to do',
        'categories.viewAll': 'View all Categories →',
        'categories.viewAllTools': 'View all tools →',
        'home.courseContent.title': 'Course content',
        'home.courseContent.subtitle': 'Follow the levels in order each one unlocks real skills you can use at work.',
        'home.courseContent.roadmapAlt': 'Course roadmap',
        'home.courseContent.level1.title': 'Level 1 - Foundations of Applied Intelligence',
        'home.courseContent.level1.bullet1': 'Understand how AI "thinks" (without the math headache)',
        'home.courseContent.level1.bullet2': 'Build the right mindset + best practices to learn fast',
        'home.courseContent.level1.bullet3': 'Master prompting for everyday workflows',
        'home.courseContent.level1.bullet4': 'Ship your first mini AI project quickly',
        'home.courseContent.level2.title': 'Level 2 - Applied AI for Academic & Career Excellence',
        'home.courseContent.level2.bullet1': 'Study + learn faster using AI systems (not random prompts)',
        'home.courseContent.level2.bullet2': 'Do better research: deeper thinking, less noise',
        'home.courseContent.level2.bullet3': 'Write better papers/content with clarity + structure',
        'home.courseContent.level2.bullet4': 'Build memory systems that help you retain what you learn',
        'home.courseContent.level2.bullet5': 'Create presentations faster without losing quality',
        'home.courseContent.level2.bullet6': 'Build a real "career advantage" using AI',
        'home.courseContent.level3.title': 'Level 3 - Corporate Applied Intelligence & Automation',
        'home.courseContent.level3.bullet1': 'Audit your time + convert tasks into AI workflows',
        'home.courseContent.level3.bullet2': 'Upgrade communication + sales execution with AI',
        'home.courseContent.level3.bullet3': 'Launch marketing output 10x faster (without becoming cringe)',
        'home.courseContent.level3.bullet4': 'Build reporting & dashboards using AI assistance',
        'home.courseContent.level3.bullet5': 'Automate meetings + knowledge capture',
        'home.courseContent.level3.bullet6': 'Build your own AI assistant (personal productivity agent)',
        'home.courseContent.level4.title': 'Level 4 - AI Leadership & Scalable Systems Certification',
        'home.courseContent.level4.bullet1': 'Design teams of AI "agents" (roles + handoffs)',
        'home.courseContent.level4.bullet2': 'Decision-making playbooks with AI support',
        'home.courseContent.level4.bullet3': 'Department-level automation (repeatable ops, not hacks)',
        'home.courseContent.level4.bullet4': 'Learn how AI drives product innovation',
        'home.courseContent.level4.bullet5': 'Build the systems mindset needed to scale',
        'home.courseContent.level5.title': 'Level 5 - AI for Strategy & Operations',
        'home.courseContent.level5.bullet1': 'Think in futures: strategy using AI as a co-pilot',
        'home.courseContent.level5.bullet2': 'Improve ops execution with AI optimization',
        'home.courseContent.level5.bullet3': 'Forecasting + performance intelligence workflows',
        'home.courseContent.level5.bullet4': 'Supply chain + KPI automation concepts',
        'home.courseContent.level5.bullet5': 'Executive reporting that\'s clean, fast, and decision-ready',
        'home.courseContent.level6.title': 'Level 6 - Digital Transformation, Marketing & Sales AI',
        'home.courseContent.level6.bullet1': 'Run a digital transformation plan with AI support',
        'home.courseContent.level6.bullet2': 'Use AI tools for consistent content + distribution',
        'home.courseContent.level6.bullet3': 'Build smarter sales pipelines + automation',
        'home.courseContent.level6.bullet4': 'Automate support + customer experience workflows',
        'home.courseContent.level6.bullet5': 'Track commercial impact (so AI work actually "counts")',
        'home.courseContent.level7.title': 'Level 7 - 10× AI for Entrepreneurs & Business Leaders',
        'home.courseContent.level7.bullet1': 'Shift from founder doing tasks -> operator building systems',
        'home.courseContent.level7.bullet2': 'Client acquisition + sales workflows powered by AI',
        'home.courseContent.level7.bullet3': 'Marketing systems that run while you work',
        'home.courseContent.level7.bullet4': 'Internal ops + productivity at scale',
        'home.courseContent.level7.bullet5': 'Cross-team automation playbooks',
        'home.courseContent.level7.bullet6': 'Build a lean AI-powered company (realistically)',
        'home.courseContent.level8.title': 'Level 8 - 10× AI for Finance, Trading & Capital Intelligence',
        'home.courseContent.level8.bullet1': 'Think financially in the AI era (signals, narratives, risk)',
        'home.courseContent.level8.bullet2': 'Macro + market research workflows with AI',
        'home.courseContent.level8.bullet3': 'Risk + scenario design (decision frameworks)',
        'home.courseContent.level8.bullet4': 'Portfolio construction workflows',
        'home.courseContent.level8.bullet5': 'Trading systems + execution discipline concepts',
        'home.courseContent.level8.bullet6': 'Build an "AI-augmented investor" workflow',
        'news.previewTitle': 'News & Insights Preview',
        'news.previewDescription': 'Stay sharp with updates, tutorials and analyses across AI tools + hardware.',
        'news.exploreMore': 'Explore More →',
        'news.defaultLabel': 'News',
        'news.noNewsFound': 'No News Found',
        'news.insights': 'INSIGHTS',
        'news.aiAndCreativeExpression': 'AI and Creative Expression',
        'faq.title': 'Frequently Asked',
        'faq.titleHighlight': 'Questions',
        'faq.description': 'Find Answers to all your questions.',
        'faq.viewMore': 'View More',
        'faq.viewAll': 'View All FAQs',
        'faq.noFaqsAvailable': 'No FAQs available at the moment.',

        // News
        'news.noNewsSubtitle': 'No news articles available at the moment. Check back later for the latest updates.',
        
        // My Courses
        'myCourses.all': 'All',
        'myCourses.inProgress': 'In progress',
        'myCourses.completed': 'Completed',
        'myCourses.searchPlaceholder': 'Search courses...',
        'myCourses.noCoursesFound': 'No courses found',
        'myCourses.failedToLoad': 'Failed to load courses',
        'myCourses.notFound': 'Course not found',
        'myCourses.courses': 'courses',
        'myCourses.recommendedNext': 'Recommended next',
        'myCourses.noRecommendations': 'Keep learning to see personalized recommendations.',
        'myCourses.yourLearningJourney': 'Your learning journey',
        'myCourses.progressAtAGlance': 'Progress at a glance',
        'myCourses.totalCourses': 'Total courses',
        'myCourses.completedCourses': 'Completed courses',
        'myCourses.inProgressCourses': 'In progress courses',
        'myCourses.notStartedCourses': 'Not started courses',
        'myCourses.lockedCourses': 'Locked courses',
        'myCourses.statusCurrent': 'Current',
        'myCourses.level1Title': 'Level 1: Foundations of Applied Intelligence',
        'myCourses.level2Title': 'Level 2: Applied AI for Academic & Career Excellence',
        'myCourses.level3Title': 'Level 3: Corporate Applied Intelligence & Automation',
        'myCourses.level4Title': 'Level 4: AI Leadership & Scalable Systems Certification',
        'myCourses.level5Title': 'Level 5: AI for Strategy and Operations',
        'myCourses.level6Title': 'Level 6: Digital Transformation, Marketing & Sales AI',
        'myCourses.level7Title': 'Level 7: 10× AI for Entrepreneurs & Business Leaders',
        'myCourses.level8Title': 'Level 8: 10× AI for Finance, Trading & Capital Intelligence',
        'myCourses.progress': 'Progress',
        'myCourses.subtitle': 'A cleaner, level-based learning journey — fewer distractions, clearer progression, and instantly scannable levels.',
        'myCourses.certificateInfo': 'Complete a level to earn a certificate',
        
        // Community
        'community.recentlyAdded': 'Recently added',
        'community.trending': 'Trending',
        'community.unanswered': 'Unanswered',
        'community.askQuestion': 'Ask Question',
        'community.postTitlePlaceholder': 'Enter your question title...',
        'community.postContentPlaceholder': 'What\'s on your mind?',
        'community.submitQuestion': 'Submit Question',
        'community.cancel': 'Cancel',
        
        // Rewards
        'rewards.viewAll': 'View All',
        'rewards.quizPass': 'Quiz Pass',
        'rewards.levelComplete': 'Level Complete',
        'rewards.promptUnlock': 'Prompt Unlock',
        'rewards.bonus': 'Bonus',
        'rewards.reason': 'Unlocked prompt for level completion',
        
        // Events
        'events.recentlyAdded': 'Recently added',
        'events.startingSoon': 'Starting soon',
        'events.pastEvents': 'Past events',
        'events.searchPlaceholder': 'Search events...',
    },
    de: {
        // Common
        'common.loading': 'Lädt…',
        'common.loadingData': 'Daten werden geladen…',
        'common.error': 'Fehler',
        'common.networkError': 'Netzwerkfehler, bitte erneut versuchen',
        'common.success': 'Erfolg',
        'common.cancel': 'Abbrechen',
        'common.save': 'Speichern',
        'common.saveChanges': 'Änderungen speichern',
        'common.saving': 'Speichern...',
        'common.delete': 'Löschen',
        'common.edit': 'Bearbeiten',
        'common.close': 'Schließen',
        'common.submit': 'Absenden',
        'common.next': 'Weiter',
        'common.previous': 'Zurück',
        'common.show': 'Anzeigen',
        'common.hide': 'Ausblenden',
        'common.back': 'Zurück',
        'common.continue': 'Fortfahren',
        'common.download': 'Herunterladen',
        'common.downloading': 'Wird heruntergeladen...',
        'common.share': 'Teilen',
        'common.search': 'Suchen',
        'common.clearSearch': 'Suche löschen',
        'common.filter': 'Filter',
        'common.viewAll': 'Alle anzeigen',
        'common.noResults': 'Keine Ergebnisse gefunden',
        'common.tryAgain': 'Erneut versuchen',
        'common.readMore': 'Mehr lesen',
        'common.untitled': 'Ohne Titel',
        'common.sortBy': 'Sortieren nach',
        'common.sortByLabel': 'Sortieren nach :',
        'common.searchByName': 'Nach Name suchen',
        'common.searchByNameShort': 'Nach Name suchen',
        'common.recentlyAdded': 'Kürzlich hinzugefügt',
        'common.mostPopular': 'Beliebteste',
        'common.oldestFirst': 'Älteste zuerst',
        'common.optional': 'Optional',
        'common.total': 'Gesamt',
        'common.progress': 'Fortschritt',
        'common.answered': 'Beantwortet',
        'common.view': 'Ansehen',

        // Questionnaire
        'questionnaire.title': 'Vervollständige dein AIRIES-Profil',
        'questionnaire.badge': 'KI-Bereitschaftstest',
        'questionnaire.loading': 'Dein Fragebogen wird vorbereitet...',
        'questionnaire.progress': 'Frage {current} von {total}',
        'questionnaire.instruction': 'Wähle die Antwort, die am besten passt.',
        'questionnaire.other': 'Andere Antwort',
        'questionnaire.otherPlaceholder': 'Schreibe deine Antwort',
        'questionnaire.finish': 'Mein Ergebnis anzeigen',
        'questionnaire.completed': 'Fragebogen abgeschlossen.',
        'questionnaire.submitError': 'Antworten konnten nicht gesendet werden. Versuche es erneut.',
        'questionnaire.emptyTitle': 'Keine Fragen gefunden',
        'questionnaire.emptyMessage': 'Der Fragebogen konnte nicht geladen werden. Bitte versuche es erneut.',
        
        // Navigation
        'nav.home': 'Startseite',
        'nav.tools': 'Tools',
        'nav.toolsExplained': 'Tools erklärt',
        'nav.toolsMarketplace': 'Tools-Marktplatz',
        'nav.news': 'Nachrichten',
        'nav.blogs': 'Blogs',
        'nav.newsAndBlogs': 'Nachrichten & Blogs',
        'nav.courseContent': 'Kursinhalte',
        'nav.events': 'Veranstaltungen',
        'nav.pricing': 'Preise',
        'nav.universities': 'Universitäten',
        'nav.affiliate': 'Affiliate / Partner',
        'nav.overview': 'Übersicht',
        'nav.myCourses': 'Meine Kurse',
        'nav.eventsWebinars': 'Veranstaltungen & Webinare',
        'nav.community': 'Community',
        'nav.rewards': 'Belohnungen',
        'nav.certificates': 'Zertifikate',
        'nav.profile': 'Profil',
        'nav.dashboard': 'Dashboard',
        'nav.signIn': 'Anmelden',
        'nav.getStarted': 'Loslegen',
        'nav.logout': 'Abmelden',
        'nav.viewProfile': 'Profil anzeigen',
        'nav.toggleMenu': 'Menü umschalten',
        'nav.signedInAs': 'Angemeldet als',
        'nav.signedInToAiries': 'Bei Airies angemeldet',
        'nav.contact': 'Kontakt',
        'nav.faq': 'FAQ',
        'nav.more': 'Mehr',
        
        // Dashboard Overview
        'overview.welcomeBack': 'Willkommen zurück',
        'overview.readyToContinue': 'Bereit, deine Lernreise fortzusetzen?',
        'overview.weeklyActivity': 'Wöchentliche Lernaktivität',
        'overview.hoursSpent': 'Diese Woche verbrachte Lernstunden',
        'overview.hour': 'Stunde',
        'overview.hours': 'Stunden',
        'overview.progressStatistics': 'Fortschrittsstatistiken',
        'overview.coursesCompleted': 'Abgeschlossene Kurse',
        'overview.totalCourses': 'Gesamtkurse',
        'overview.certificates': 'Zertifikate',
        'overview.studyHours': 'Lernstunden',
        'overview.completed': 'Abgeschlossen',
        'overview.continueLearning': 'Weiterlernen',
        'overview.noActiveCourses': 'Du hast noch keine aktiven Kurse. Starte einen Kurs, um ihn hier zu sehen.',
        'overview.startLearning': 'Lernen starten',
        'overview.failedToLoad': 'Dashboard konnte nicht geladen werden',
        
        // Profile
        'profile.myProfile': 'Mein Profil',
        'profile.profileInfo': 'Profilinformationen',
        'profile.settings': 'Einstellungen',
        'profile.profilePhoto': 'Profilfoto',
        'profile.firstName': 'Vorname',
        'profile.lastName': 'Nachname',
        'profile.phone': 'Telefon',
        'profile.email': 'E-Mail',
        'profile.bio': 'Biografie',
        'profile.bioPlaceholder': 'Bitte schreibe eine Biografie über dich',
        'profile.university': 'Universität',
        'profile.company': 'Unternehmen',
        'profile.profileUpdated': 'Profil erfolgreich aktualisiert!',
        'profile.failedToLoad': 'Profil konnte nicht geladen werden',
        'profile.failedToSave': 'Profil konnte nicht gespeichert werden',
        'profile.failedToUpload': 'Avatar konnte nicht hochgeladen werden',
        'profile.language': 'Sprache',
        'profile.account': 'Konto',
        'profile.changePassword': 'Passwort ändern',
        'profile.currentPassword': 'Aktuelles Passwort',
        'profile.newPassword': 'Neues Passwort',
        'profile.confirmPassword': 'Neues Passwort bestätigen',
        'profile.passwordUpdated': 'Passwort erfolgreich aktualisiert!',
        'profile.failedToChangePassword': 'Passwort konnte nicht geändert werden',
        'profile.allFieldsRequired': 'Alle Felder sind erforderlich',
        'profile.passwordsDoNotMatch': 'Neue Passwörter stimmen nicht überein',
        'profile.passwordTooShort': 'Neues Passwort muss mindestens 6 Zeichen lang sein',
        'profile.passwordSame': 'Neues Passwort muss sich vom aktuellen Passwort unterscheiden',
        'profile.changingPassword': 'Wird geändert...',
        'profile.sessions': 'Aktive Sitzungen',
        'profile.clearAllDevices': 'Alle Geräte löschen',
        'profile.clearAllDevicesConfirm': 'Alle Geräte löschen?',
        'profile.clearAllDevicesConfirmDesc': 'Du wirst auf allen Geräten abgemeldet, einschließlich diesem.',
        'profile.clearAndLogout': 'Löschen & Abmelden',
        'profile.currentDevice': 'Aktuelles Gerät',
        'profile.remove': 'Entfernen',
        'profile.subscription': 'Abonnement',
        'profile.billing': 'Abrechnung',
        'profile.alternateEmail': 'Alternative E-Mail',
        'profile.alternateEmailSameAsPrimary': 'Die alternative E-Mail darf nicht dieselbe wie deine primäre E-Mail sein.',
        'profile.address': 'Adresse',
        'profile.addressLine1': 'Adresszeile 1',
        'profile.addressLine2': 'Adresszeile 2',
        'profile.city': 'Stadt',
        'profile.state': 'Bundesland/Provinz',
        'profile.postalCode': 'Postleitzahl',
        'profile.country': 'Land',
        'profile.fieldsNoWhitespace': 'Felder dürfen nicht nur Leerzeichen enthalten',
        'profile.searchPlaceholder': 'Suchen...',
        'subscription.currentPlan': 'Aktueller Plan',
        'subscription.managedByCleverbridge': 'Abo wird von Cleverbridge verwaltet',
        'subscription.availablePlans': 'Verfügbare Pläne',
        'subscription.upgrade': 'Jetzt upgraden',
        'subscription.free': 'Kostenlos',
        'billing.noInvoices': 'Keine Abrechnungsverlauf gefunden.',
        'billing.showing': 'Zeige',
        'billing.of': 'von',
        'billing.page': 'Seite',

        // Billing (dashboard profile)
        'billing.invoice': 'Rechnung',
        'billing.date': 'Datum',
        'billing.plan': 'Tarif',
        'billing.amount': 'Betrag',
        'billing.status': 'Status',
        'billing.action': 'Aktion',
        'billing.download': 'Herunterladen',
        'billing.managedByCleverbridge': 'Abrechnung wird von Cleverbridge verwaltet.',
        'billing.downloadInvoice': 'Letzte Rechnung herunterladen',
        'billing.cleverbridgeNoInvoice': 'Abrechnung wird von Cleverbridge verwaltet. Lade Rechnungen unter Abonnement verwalten auf der Abo-Seite herunter.',
        
        // Courses
        'courses.myCourses': 'Meine Kurse',
        'courses.allCourses': 'Alle Kurse',
        'courses.inProgress': 'In Bearbeitung',
        'courses.completed': 'Abgeschlossen',
        'courses.locked': 'Gesperrt',
        'courses.noCourses': 'Keine Kurse gefunden',
        'courses.loadingCourses': 'Kurse werden geladen…',
        'courses.startLearning': 'Lernen starten',
        'courses.continueLearning': 'Weiterlernen',
        'courses.lockedMessage': 'Bitte beende Level 1',
        'courses.loadingCourse': 'Kurs wird geladen…',
        'courses.courseNotFound': 'Kurs nicht gefunden',
        'courses.overview': 'Übersicht',
        'courses.syllabus': 'Lehrplan',
        'courses.content': 'Kursinhalt',
        'courses.loadingPdf': 'PDF wird geladen...',
        'courses.failedToLoadPdf': 'PDF konnte nicht geladen werden',
        'courses.failedToLoad': 'Kurs konnte nicht geladen werden',
        'courses.whatYouWillLearn': 'Was du lernen wirst',
        'courses.courseQuiz': 'Kurs-Quiz',
        'courses.invalidVideoConfiguration': 'Ungültige Videokonfiguration',
        'courses.lessons': 'Lektionen',
        'courses.quiz': 'Quiz',
        'courses.takeQuiz': 'Quiz starten',
        'courses.quizTitle': 'Kurs-Quiz',
        'courses.questionOf': 'Frage',
        'courses.of': 'von',
        'courses.passMark': 'Mindestpunktzahl',
        'courses.progress': 'Fortschritt',
        'courses.answered': 'beantwortet',
        'courses.typeAnswer': 'Gib deine Antwort ein...',
        'courses.pleaseAnswerAll': 'Bitte beantworte alle Fragen vor dem Absenden.',
        'courses.checkingAnswers': 'Antworten werden geprüft…',
        'courses.submitting': 'Wird übermittelt…',
        'courses.congratulations': 'Herzlichen Glückwunsch! 🎉',
        'courses.quizSubmitted': 'Quiz übermittelt',
        'courses.youScored': 'Du hast',
        'courses.certificateGenerated': 'Dein Zertifikat wurde generiert! 🎓',
        'courses.certificateReady': 'Dein Zertifikat ist bereit',
        'courses.certificateDescription':
            'Du hast diesen Kurs erfolgreich abgeschlossen. Generiere dein Zertifikat, um es herunterzuladen und zu teilen.',
        'courses.viewCertificates': 'Du kannst es in deinem Zertifikate-Tab ansehen.',
        'courses.earnedPoints': 'Du hast',
        'courses.points': 'Punkte',
        'courses.unlockedBadge': 'verdient und das "Quiz-Meister"-Abzeichen freigeschaltet!',
        'courses.generateCertificate': 'Zertifikat erstellen',
        'courses.loadingQuiz': 'Quiz wird geladen…',
        'courses.previous': 'Zurück',
        'courses.submit': 'Absenden',
        'courses.next': 'Weiter',
        'courses.failedToSubmitQuiz': 'Quiz konnte nicht übermittelt werden',
        'courses.close': 'Schließen',
        'courses.statusNotStarted': 'Nicht begonnen',
        'courses.statusInProgress': 'In Bearbeitung',
        'courses.statusCompleted': 'Abgeschlossen',
        'courses.detailIntro': 'Bitte absolviere das Quiz, um den Kurs zu bestehen. Alle relevanten Informationen findest du im Video und in der Präsentation unten.',
        'courses.completedShort': 'abgeschlossen',
        'courses.level': 'Level',
        'courses.courseNumber': 'Kurs',
        'courses.learningPath': 'Lernpfad',
        'courses.categoryDevelopment': 'Entwicklung',
        'courses.startQuiz': 'Quiz starten',
        'courses.retakeQuiz': 'Quiz wiederholen',
        'courses.viewAnswers': 'Antworten ansehen',
        'courses.lastScore': 'Letzte Quizpunktzahl',
        'courses.reviewPassed': 'Tolle Arbeit! Du hast das Quiz bestanden 🎉',
        'courses.reviewTryAgain': 'Quiz eingereicht überprüfe deine Antworten',
        'courses.correctOutOf': 'richtige Antworten',
        'courses.noAnswer': 'Keine Antwort gegeben',
        'courses.prevCourse': 'Vorheriger Kurs',
        'courses.nextCourse': 'Nächster Kurs',
        'courses.answerCorrect': 'Richtig',
        'courses.answerIncorrect': 'Falsch',
        'courses.yourAnswer': 'Deine Antwort',
        'courses.correctAnswer': 'Richtige Antwort',
        'courses.quizPassedLabel': 'Quiz bestanden',
        'courses.quizFailedLabel': 'Quiz nicht bestanden',
        
        // Certificates
        'certificates.certificates': 'Zertifikate',
        'certificates.loading': 'Zertifikate werden geladen…',
        'certificates.noCertificates': 'Noch keine Zertifikate',
        'certificates.noCertificatesDesc': 'Sobald du alle Quizze in einem Level abschließt, erscheinen deine Zertifikate hier.',
        'certificates.issued': 'Ausgestellt',
        'certificates.type': 'Typ',
        'certificates.download': 'Herunterladen',
        'certificates.downloadPDF': 'Herunterladen',
        'certificates.share': 'Teilen',
        'certificates.shareOnLinkedIn': 'Auf LinkedIn teilen',
        'certificates.failedToLoad': 'Zertifikate konnten nicht geladen werden',
        'certificates.failedToDownload': 'Zertifikat konnte nicht heruntergeladen werden',
        'certificates.failedToShare': 'Freigabelink konnte nicht abgerufen werden',
        'certificates.certificate': 'Zertifikat',
        'certificates.ofCompletion': 'Der Vollendung',
        'certificates.thisIsToCertify': 'Hiermit wird bescheinigt, dass',
        'certificates.hasSuccessfullyCompleted': 'den Kurs erfolgreich abgeschlossen hat',
        'certificates.studentName': 'Schülername',
        'certificates.courseTitle': 'Kurstitel',
        'certificates.airiesTeam': 'www.airies.app',
        'certificates.level1Description': 'Grundlegende KI-Kenntnisse, Selbstvertrauen und angewandte Einsteiger-Skills.',
        'certificates.verification.title': 'Airies Zertifikatsverifizierung',
        'certificates.verification.verified': 'Verifiziert',
        'certificates.verification.notFound': 'Zertifikat nicht gefunden',
        'certificates.verification.notFoundDesc': 'Dieser Verifizierungslink ist ungültig oder das Zertifikat wurde widerrufen.',
        'certificates.verification.error': 'Verifizierungsfehler',
        'certificates.verification.errorDesc': 'Wir konnten dieses Zertifikat derzeit nicht verifizieren. Bitte versuche es später erneut.',
        'certificates.verification.loading': 'Zertifikat wird verifiziert…',
        'certificates.verification.certificateCode': 'Zertifikatscode',
        'certificates.verification.issuedOn': 'Ausgestellt am',
        'certificates.verification.shareOnLinkedIn': 'Auf LinkedIn teilen',
        'certificates.verification.downloadPDF': 'Herunterladen',
        'certificates.verification.authenticityNote': 'Diese Seite bestätigt die Authentizität eines Airies-Zertifikats.',
        'certificates.level2Description': 'Beherrschung von KI-Tools für Studium, Schreiben, Recherche, Produktivität und frühe Karriereentwicklung.',
        'certificates.level3Description': 'Die lernende Person kann KI bei der Arbeit einsetzen, Workflows automatisieren, Kommunikation verbessern und echte Produktivitätssysteme aufbauen.',
        'certificates.level4Description': 'Die lernende Person kann KI-Systeme entwerfen, Teams mit Automatisierung führen und skalierbare KI-gesteuerte Operationen aufbauen.',
        'certificates.preparingDownload': 'Download wird vorbereitet...',
        'certificates.loadingBackground': 'Hintergrundbild wird geladen...',
        'certificates.convertingBackground': 'Hintergrundbild wird konvertiert...',
        'certificates.generatingCertificate': 'Zertifikat wird generiert...',
        'certificates.loadingImages': 'Bilder werden geladen...',
        'certificates.finalizing': 'Wird abgeschlossen...',
        'certificates.rendering': 'Zertifikat wird gerendert...',
        'certificates.downloading': 'Wird heruntergeladen...',
        'certificates.linkedinInstructionsTitle': 'So fügst du dieses Zertifikat zu deinem LinkedIn-Profil hinzu',
        'certificates.linkedinNameLabel': 'Name',
        'certificates.linkedinOrgLabel': 'Ausstellende Organisation',
        'certificates.linkedinIssueDateLabel': 'Ausstellungsdatum',
        'certificates.linkedinExpirationLabel': 'Ablauf',
        'certificates.linkedinCredentialIdLabel': 'Credential-ID',
        'certificates.linkedinCredentialUrlLabel': 'Credential-URL',
        'certificates.verificationUrl': 'Verifizierungs-URL',
        'certificates.linkedinNoExpiration': 'Kein Ablaufdatum',
        'certificates.linkedinShareSummary': 'Ich habe {name} auf AIRIES abgeschlossen!',
        'certificates.issuingOrganization': 'Airies GmbH',
        
        // Video Players
        'video.loading': 'Video wird geladen...',
        'video.failedToLoadLibrary': 'Video-Player-Bibliothek konnte nicht geladen werden.',
        'video.browserNotSupported': 'Dein Browser unterstützt keine DRM-geschützte Videowiedergabe.',
        'video.errorOccurred': 'Beim Abspielen des Videos ist ein Fehler aufgetreten.',
        'video.licenseFailed': 'Video-Lizenz konnte nicht abgerufen werden. Bitte überprüfe deine Authentifizierung.',
        'video.loadFailed': 'Video konnte nicht geladen werden. Bitte überprüfe deine Internetverbindung.',
        'video.drmUnavailable': 'DRM-System ist in deinem Browser nicht verfügbar.',
        'video.tryRefreshing': 'Bitte versuche, die Seite zu aktualisieren, oder kontaktiere den Support, wenn das Problem weiterhin besteht.',
        'video.invalidPlaybackId': 'Ungültige Wiedergabe-ID',
        'video.invalidVimeoUrl': 'Ungültige Vimeo-URL. Bitte gib eine gültige Vimeo-Video-URL oder -ID an.',
        'video.failedToInitialize': 'Vimeo-Player konnte nicht initialisiert werden.',
        'video.checkVimeoUrl': 'Bitte überprüfe die Vimeo-URL und versuche es erneut.',

        // Tools Explained
        'toolsExplained.title': 'Tools erklärt',
        'toolsExplained.empty': 'Noch keine Tools erklärt.',
        'toolsExplained.watch': 'Ansehen',
        'toolsExplained.moreToCome': 'Mehr kommt bald',
        
        // Share
        'share.shareTool': 'Tool teilen',
        'share.linkCopied': 'Link in die Zwischenablage kopiert!',
        'share.linkCopiedForInstagram': 'Link kopiert! Öffne Instagram und füge ihn zum Teilen ein.',
        'share.failedToCopy': 'Link konnte nicht kopiert werden',
        
        // Community
        'community.community': 'Community',
        'community.stats': 'Community-Statistiken',
        'community.totalPosts': 'Gesamtbeiträge',
        'community.totalComments': 'Gesamtkommentare',
        'community.activeUsers': 'Aktive Benutzer',
        'community.rooms': 'Räume',
        'community.posts': 'Beiträge',
        'community.loadingPosts': 'Beiträge werden geladen…',
        'community.noPosts': 'Keine Beiträge gefunden',
        'community.noPostsSubtitle': 'Sei die Erste oder der Erste, der in diesem Raum postet!',
        'community.createPost': 'Beitrag erstellen',
        'community.postTitle': 'Beitragstitel',
        'community.postContent': 'Beitragsinhalt',
        'community.postPlaceholder': 'Was denkst du?',
        'community.submitPost': 'Beitrag absenden',
        'community.reply': 'Antworten',
        'community.replies': 'Antworten',
        'community.loadingReplies': 'Antworten werden geladen…',
        'community.writeReply': 'Schreibe eine Antwort...',
        'community.submitReply': 'Antwort absenden',
        'community.like': 'Gefällt mir',
        'community.unlike': 'Gefällt mir nicht mehr',
        'community.comment': 'Kommentar',
        'community.comments': 'Kommentare',
        'community.viewReplies': 'Antworten anzeigen',
        'community.hideReplies': 'Antworten ausblenden',
        'community.failedToLoad': 'Community-Daten konnten nicht geladen werden',
        'community.failedToPost': 'Beitrag konnte nicht erstellt werden',
        'community.failedToReply': 'Antwort konnte nicht erstellt werden',
        'community.failedToUploadAttachment': 'Anhang konnte nicht hochgeladen werden',
        'community.errorMissingContent': 'Bitte füge Inhalt für deinen Beitrag hinzu.',
        'community.errorSelectRoom': 'Bitte wähle zuerst einen Raum aus.',
        'community.errorRoomNotFound': 'Raum nicht gefunden',
        'community.searchPlaceholder': 'Nach Name suchen',
        'community.activeRooms': 'Aktive Räume',
        'community.noRepliesYet': 'Noch keine Antworten. Sei die Erste oder der Erste, der antwortet!',
        'community.selectRoomLabel': 'Raum auswählen',
        'community.selectRoomPlaceholder': 'Wähle einen Raum',
        'community.linkLabel': 'Link (optional)',
        'community.linkTitleLabel': 'Linktitel',
        'community.linkImageUrlLabel': 'Link-Bild-URL',
        'community.linkDescriptionLabel': 'Linkbeschreibung',
        'community.linkDescriptionPlaceholder': 'Kurze Zusammenfassung, die in der Vorschau angezeigt wird',
        'community.attachmentsLabel': 'Anhänge (Bilder / Dokumente)',
        'community.uploadingAttachment': 'Wird hochgeladen…',
        'community.submittingQuestion': 'Wird veröffentlicht...',
        
        // Rewards
        'rewards.rewards': 'Belohnungen',
        'rewards.summary': 'Belohnungsübersicht',
        'rewards.history': 'Belohnungsverlauf',
        'rewards.prompts': 'Freigeschaltete Prompts',
        'rewards.howToUnlockPrompts': 'Schalte Prompts frei, indem du Levels abschließt. Jedes Level, das du bestehst, schaltet einen neuen Prompt frei.',
        'rewards.totalPoints': 'Gesamtpunkte',
        'rewards.availablePoints': 'Verfügbare Punkte',
        'rewards.redeemedPoints': 'Eingelöste Punkte',
        'rewards.recentActivity': 'Letzte Aktivität',
        'rewards.loadingHistory': 'Verlauf wird geladen…',
        'rewards.noHistory': 'Noch kein Belohnungsverlauf',
        'rewards.startLearning': 'Beginne zu lernen, um Belohnungen zu verdienen!',
        'rewards.points': 'Punkte',
        'rewards.earned': 'Verdient',
        'rewards.redeemed': 'Eingelöst',
        'rewards.failedToLoad': 'Belohnungsdaten konnten nicht geladen werden',
        'rewards.quizScoreDescription': 'Du hast {score}% im Quiz erreicht',
        'rewards.tierPlatinum': 'Platin',
        'rewards.tierGold': 'Gold',
        'rewards.tierSilver': 'Silber',
        'rewards.learnerSuffix': 'Lerner',
        'rewards.bannerPlatinumAchieved': 'Platin-Lerner erreicht!',
        'rewards.bannerToPlatinum': 'Steige zum Platin-Lerner auf',
        'rewards.bannerToGold': 'Steige zum Gold-Lerner auf',
        'rewards.currentPoints': 'Aktuelle Punkte',
        'rewards.pointsUntilNext': '{points} Punkte bis zum nächsten Level',
        'rewards.maxLevelReached': 'Maximales Level erreicht!',
        'rewards.lifetimePoints': 'Lifetime-Punkte',
        'rewards.unlockedPrompts': 'Freigeschaltete Prompts',
        'rewards.singlePromptUnlocked': '1 Prompt freigeschaltet',
        'rewards.multiplePromptsUnlocked': '{count} Prompts freigeschaltet',
        'rewards.unlockedAt': 'Freigeschaltet {timeAgo}',
        'rewards.noPromptsTitle': 'Noch keine Prompts freigeschaltet',
        'rewards.noPromptsSubtitle': 'Schließe alle Kurse in einem Level ab, um Premium-Prompts freizuschalten',
        'rewards.copyPrompt': 'Prompt kopieren',
        'rewards.copyPromptSuccess': 'Prompt in die Zwischenablage kopiert',
        'rewards.copyPromptError': 'Prompt konnte nicht kopiert werden',
        
        // Events
        'events.events': 'Veranstaltungen',
        'events.loading': 'Veranstaltungen werden geladen…',
        'events.noEvents': 'Keine Veranstaltungen gefunden',
        'events.upcoming': 'Bevorstehend',
        'events.past': 'Vergangen',
        'events.saveSeat': 'Platz reservieren',
        'events.viewDetails': 'Details anzeigen',
        'events.failedToLoad': 'Veranstaltungen konnten nicht geladen werden',
        'events.subtitle': 'Entdecke bevorstehende und vergangene AI-Events, Webinare und Konferenzen.',
        'events.upcomingTitle': 'Bevorstehende Veranstaltungen & Updates',
        'events.viewAllUpdates': 'Alle Updates anzeigen',
        'events.eventLabel': 'Veranstaltung',
        'events.noEventsFound': 'Keine Veranstaltungen gefunden',
        'events.noEventsSubtitle': 'Bevorstehende Veranstaltungen und Webinare deiner Universität werden hier angezeigt.',
        
        // Notifications
        'notifications.notifications': 'Benachrichtigungen',
        'notifications.loading': 'Benachrichtigungen werden geladen…',
        'notifications.noNotifications': 'Keine Benachrichtigungen',
        'notifications.markAllRead': 'Alle als gelesen markieren',
        'notifications.failedToLoad': 'Benachrichtigungen konnten nicht geladen werden',
        'notifications.allCaughtUp': 'Du bist auf dem neuesten Stand!',
        'notifications.viewAll': 'Alle Benachrichtigungen anzeigen',
        'notifications.markAllReadShort': 'Alle als gelesen markieren',
        
        // Pricing
        'pricing.comparePlans': 'Pläne vergleichen',
        'pricing.compareBestPlans': 'Vergleiche unsere besten Pläne zusammen',
        'pricing.plansAvailable': 'Pläne verfügbar',
        'pricing.plan': 'Plan',
        'pricing.features': 'Funktionen',
        'pricing.joinWaitlist': 'Warteliste beitreten',
        'pricing.failedToLoad': 'Preispläne konnten nicht geladen werden',
        'pricing.label': 'Preise',
        'pricing.title': 'Flexible Pläne für jeden Bedarf',
        'pricing.description': 'Wähle den Plan, der zu deiner KI-Reise passt starte kostenlos, wachse oder skaliere mit unseren besten Plänen.',
        'pricing.perMonth': 'pro Monat',
        'pricing.perYear': 'pro Jahr',
        'pricing.billedMonthly': 'Monatlich abgerechnet',
        'pricing.billedAnnually': 'Jährlich abgerechnet',
        'pricing.exclVat': 'exkl. MwSt.',
        'pricing.discountPercent': '% RABATT',
        'pricing.compareAllPlans': 'Alle Pläne vergleichen',
        'pricing.notOpenForIndividuals': 'Hinweis: Die Plattform ist derzeit noch nicht für Einzelpersonen geöffnet. Bitte kontaktiere uns für Team- oder institutionellen Zugang.',
        'pricing.mostPopular': 'Beliebtester',
        'pricing.planFallback': 'Plan',
        'pricing.featuresComingSoon': 'Feature-Details folgen in Kürze.',
        'pricing.included': 'Enthalten',
        'pricing.notIncluded': 'Nicht enthalten',
        'pricing.continueToCheckout': 'Weiter zur Zahlung',
        'pricing.getFullAccess': 'Jetzt Vollzugang sichern',
        'pricing.getCorporateAccess': 'Jetzt Unternehmenszugang sichern',
        'pricing.haveLicenseKey': 'Hast du einen Lizenzschlüssel?',
        
        // Contact
        'contact.title': 'Kontakt',
        'contact.subtitle': 'Erzähle uns ein wenig über dich und wie wir helfen können – unser Team meldet sich bald bei dir.',
        'contact.firstNameLabel': 'Vorname',
        'contact.firstNamePlaceholder': 'Vorname',
        'contact.lastNameLabel': 'Nachname',
        'contact.lastNamePlaceholder': 'Nachname',
        'contact.emailLabel': 'E-Mail',
        'contact.emailPlaceholder': 'Unternehmens-oder Universitäts-E-Mail',
        'contact.phoneLabel': 'Telefon',
        'contact.phonePlaceholder': 'Gib deine Telefonnummer ein',
        'contact.inquiryCategoryLabel': 'Anfragekategorie',
        'contact.inquiryCategoryHelp': 'Das Formular wird aktualisiert, um relevante Felder für deine Auswahl anzuzeigen.',
        'contact.companyNameLabel': 'Firmenname',
        'contact.companyNamePlaceholder': 'Dein Unternehmen',
        'contact.websiteLabel': 'Website / Domain',
        'contact.websitePlaceholder': 'https://beispiel.com',
        'contact.productNameLabel': 'Produktname',
        'contact.productNamePlaceholder': 'Dein Produktname',
        'contact.productUrlLabel': 'Produkt-URL',
        'contact.toolCategoryLabel': 'Kategorie',
        'contact.toolCategoryPlaceholder': 'Kategorie auswählen',
        'contact.toolCategory.nlp': 'NLP',
        'contact.toolCategory.vision': 'Vision',
        'contact.toolCategory.agents': 'Agenten',
        'contact.toolCategory.analytics': 'Analytics',
        'contact.toolCategory.robotics': 'Robotik',
        'contact.monetisationModelLabel': 'Monetarisierungsmodell',
        'contact.monetisationModelPlaceholder': 'Modell auswählen',
        'contact.monetisation.free': 'Kostenlos',
        'contact.monetisation.freemium': 'Freemium',
        'contact.monetisation.subscription': 'Abonnement',
        'contact.monetisation.oneTime': 'Einmalig',
        'contact.valuePropositionLabel': 'Value Proposition (max. 240 Zeichen)',
        'contact.valuePropositionPlaceholder': 'Warum dein Produkt wichtig ist',
        'contact.commercialNumberLabel': 'Handelsregister- / HR-Nummer',
        'contact.commercialNumberPlaceholder': 'Deine Geschäftsnummer',
        'contact.industryLabel': 'Branche',
        'contact.industryPlaceholder': 'Branche auswählen',
        'contact.industry.software': 'Software',
        'contact.industry.hardware': 'Hardware',
        'contact.industry.media': 'Medien',
        'contact.industry.education': 'Bildung',
        'contact.industry.finance': 'Finanzen',
        'contact.industry.other': 'Andere',
        'contact.productModelLabel': 'Produktreihe / Modell',
        'contact.productModelPlaceholder': 'Modellname / SKU',
        'contact.datasheetUrlLabel': 'Datenblatt- / Spezifikations-URL',
        'contact.certificationsLabel': 'Zertifizierungen',
        'contact.certificationsPlaceholder': 'Zertifikat auswählen',
        'contact.integrationInterestLabel': 'Integrationsinteresse',
        'contact.integrationInterestPlaceholder': 'Beschreibe Integrationsanforderungen & Plattforminteressen',
        'contact.objectiveLabel': 'Ziel',
        'contact.objectivePlaceholder': 'Ziel auswählen',
        'contact.objective.branding': 'Branding',
        'contact.objective.leads': 'Leadgenerierung',
        'contact.objective.launch': 'Launch',
        'contact.objective.event': 'Event',
        'contact.budgetRangeLabel': 'Budgetrahmen',
        'contact.budgetRangePlaceholder': 'Budgetrahmen auswählen',
        'contact.budget.free': 'Kostenlos',
        'contact.budget.1to5k': '1.000–5.000 €',
        'contact.budget.5to20k': '5.000–20.000 €',
        'contact.budget.20kPlus': '20.000 €+',
        'contact.activationGoalsLabel': 'Aktivierungsziele',
        'contact.activationGoalsPlaceholder': 'Markenbekanntheit, Demos & Leadgenerierung',
        'contact.publicationLabel': 'Medium / Publikation',
        'contact.publicationPlaceholder': 'Publikation oder Medium',
        'contact.deadlineLabel': 'Deadline',
        'contact.coverageTypeLabel': 'Berichterstattungstyp',
        'contact.coverageTypePlaceholder': 'Typ auswählen',
        'contact.coverageType.feature': 'Feature',
        'contact.coverageType.news': 'News',
        'contact.coverageType.interview': 'Interview',
        'contact.coverageType.review': 'Review',
        'contact.eventNameLabel': 'Eventname & Veranstalter',
        'contact.eventNamePlaceholder': 'Eventname & Veranstalter',
        'contact.eventStartDateLabel': 'Startdatum',
        'contact.eventEndDateLabel': 'Enddatum',
        'contact.eventVenueLabel': 'Veranstaltungsort',
        'contact.eventVenuePlaceholder': 'Adresse oder online',
        'contact.countryCodeLabel': 'Ländervorwahl',
        'contact.submitButton': 'Anfrage absenden',
        'contact.eventUrlLabel': 'Event- & Ticketing-URL',
        'contact.eventCategoryLabel': 'Kategorie',
        'contact.eventCategoryPlaceholder': 'Kategorie auswählen',
        'contact.eventCategory.conference': 'Konferenz',
        'contact.eventCategory.workshop': 'Workshop',
        'contact.eventCategory.meetup': 'Meetup',
        'contact.eventDescriptionLabel': 'Beschreibung',
        'contact.eventDescriptionPlaceholder': 'Kurze Eventbeschreibung',
        'contact.issueTypeLabel': 'Art des Problems',
        'contact.issueTypePlaceholder': 'Problem auswählen',
        'contact.issueType.ticketing': 'Ticketing',
        'contact.issueType.access': 'Zugang / Login',
        'contact.issueType.accessibility': 'Barrierefreiheit',
        'contact.detailsLabel': 'Details',
        'contact.detailsPlaceholder': 'Kurze Problembeschreibung',
        'contact.otherMessageLabel': 'Wie können wir helfen?',
        'contact.otherMessagePlaceholder': 'Teile alle Details zu deiner Anfrage',
        'contact.messageLabel': 'Nachricht',
        'contact.messagePlaceholder': 'Bitte gib Details an',
        'contact.errorRequiredFields': 'Bitte fülle alle Pflichtfelder aus.',
        'contact.invalidEmail': 'Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@firma.com)',
        'contact.invalidPhone': 'Bitte gib eine gültige Telefonnummer ein (mindestens 6 Ziffern)',
        'contact.successMessage': 'Danke! Deine Anfrage wurde übermittelt.',
        'contact.genericError': 'Beim Senden des Formulars ist etwas schiefgelaufen.',
        'contact.submitting': 'Wird gesendet…',
        'contact.submitEnquiry': 'Anfrage absenden',
        'contact.prefilledTopicLabel': 'Anfragethema',
        'contact.prefilledTopics.affiliate-program': 'Affiliate-Programm',
        'contact.prefilledTopics.student-ambassador': 'Student-Ambassador-Programm',
        'contact.prefilledMessages.affiliate-program': 'Ich möchte mich für das Airies Affiliate-Programm bewerben. Bitte teile mir die nächsten Schritte mit.',
        'contact.prefilledMessages.student-ambassador': 'Ich möchte mich für das Airies Student-Ambassador-Programm bewerben. Bitte teile mir die nächsten Schritte mit.',
        'contact.universityNameLabel': 'Universitätsname',
        'contact.universityNamePlaceholder': 'z.B. Oxford University',
        'contact.universityDomainLabel': 'Universitätsdomain',
        'contact.universityDomainPlaceholder': 'z.B. oxford.edu',
        'contact.universityAdminNameLabel': 'Administrator-Name',
        'contact.universityAdminNamePlaceholder': 'Vollständiger Name des Administrators',
        'contact.universityAdminEmailLabel': 'Administrator-E-Mail',
        'contact.universityAdminEmailPlaceholder': 'admin@universitaet.edu',
        'contact.universityAdminPhoneLabel': 'Administrator-Telefon',
        'contact.universityAdminPhonePlaceholder': '+44 9900 12345',
        'contact.universitySizeLabel': 'Universitätsgröße',
        'contact.universitySizePlaceholder': 'z.B. 1000–5000 Studierende',
        'contact.universityCountryLabel': 'Land',
        'contact.universityCountryPlaceholder': 'z.B. UK',
        'contact.universityNotesLabel': 'Zusätzliche Notizen',
        'contact.universityNotesPlaceholder': 'Zusätzliche Informationen über deine Universität',
        'contact.tabs.partnerships': 'Partnerschaften (B2B)',
        'contact.tabs.submitTool': 'Dein AI-Tool einreichen',
        'contact.tabs.university': 'Universitäten und Co-orporate',
        'contact.tabs.robotics': 'Robotik & Hardware',
        'contact.tabs.ads': 'Werbung & Sponsoring',
        'contact.tabs.press': 'Presse & Medien',
        'contact.tabs.support': 'Support / Private Anfrage',
        'contact.tabs.events': 'Events (Sponsor / Speaker / Listing)',
        'contact.tabs.other': 'Sonstiges',
        'contact.common.other': 'Sonstiges',
        'support.buttonLabel': 'Support',
        'support.title': 'Support erhalten',
        'support.nameLabel': 'Name',
        'support.namePlaceholder': 'Dein vollständiger Name',
        'support.emailLabel': 'E-Mail',
        'support.emailPlaceholder': 'deine@email.de',
        'support.subjectLabel': 'Betreff',
        'support.subjectPlaceholder': 'Kurze Beschreibung deines Problems',
        'support.messageLabel': 'Nachricht',
        'support.messagePlaceholder': 'Bitte gib Details zu deinem Problem an...',
        'support.categoryLabel': 'Kategorie',
        'support.categoryPlaceholder': 'Kategorie auswählen',
        'support.category.general': 'Allgemeine Frage',
        'support.category.bug': 'Fehler',
        'support.category.account': 'Kontoproblem',
        'support.category.other': 'Sonstiges',
        'support.submitButton': 'An Support senden',
        'support.submitting': 'Wird gesendet...',
        'support.successTitle': 'Danke!',
        'support.successMessage': 'Unser Support-Team wird sich bald bei dir melden.',
        'support.errorMessage': 'Etwas ist schiefgelaufen, bitte versuche es erneut.',
        'support.close': 'Schließen',
        'support.required': 'Dieses Feld ist erforderlich',
        
        // Universities
        'universities.title': 'Partneruniversitäten',
        'universities.subtitle': 'Wir bieten kostenlosen Zugang für Studierende und Mitarbeitende führender Universitäten. Steht deine Universität nicht auf der Liste oder hast du Probleme, dich anzumelden? Wir helfen dir gerne weiter, indem du auf den Button rechts klickst (Erhalte Zugang…) und das Formular ausfüllst.',
        'universities.applyButton': 'Erhalte Zugang für deine Universität',
        'universities.loading': 'Universitäten werden geladen…',
        'universities.failedToLoad': 'Universitäten konnten nicht geladen werden',
        'universities.emptyTitle': 'Keine Universitäten gefunden',
        'universities.emptySubtitle': 'Sobald Universitäten mit AIRIES verbunden sind, erscheinen sie hier.',
        'universities.searchPlaceholder': 'Suche Universität…',
        'universities.noResults': 'Keine Universitäten gefunden',
        'universities.noResultsDesc': 'Versuche, nach etwas anderem zu suchen oder lösche deine Suche.',
        'universities.clearSearch': 'Suche löschen',
        'universities.showingResults': 'Zeige',
        'universities.result': 'Ergebnis',
        'universities.results': 'Ergebnisse',
        'universities.for': 'für',
        'universities.university': 'Universität',
        'universities.universities': 'Universitäten',
        'universities.marqueeHeading': 'Kostenloser Universitätszugang',
        'universities.marqueeTitle': 'Vertraut von Studierenden und Teams an Universitäten in ganz Europa',
        'universities.viewAll': 'Alle Universitäten anzeigen',
        
        // Cookies
        'cookies.title': 'Wir respektieren deine Privatsphäre',
        'cookies.summary':
            'Wir verwenden unbedingt erforderliche Cookies, damit diese Website funktioniert, sowie optionale Analyse- und Marketing-Cookies, um die Nutzung zu verstehen und dein Erlebnis zu verbessern. Du kannst jetzt alle Cookies akzeptieren oder nicht notwendige Cookies ablehnen und deine Wahl später in unserer Cookie-Richtlinie anpassen.',
        'cookies.acceptAll': 'Alle Cookies akzeptieren',
        'cookies.reject': 'Nicht notwendige ablehnen',
        'cookies.learnMore': 'Cookie-Richtlinie',
        
        // Newsletter popup
        'newsletterPopup.title': 'Erhalte 10 % Rabatt auf dein Airies-Abo',
        'newsletterPopup.description': 'Melde dich für unseren KI-Newsletter an und erhalte jede Woche praktische Tipps, neue Tools und hilfreiche Lernressourcen.',
        'newsletterPopup.cta': '10 % Rabatt sichern',
        'newsletterPopup.skip': 'Vielleicht später',
        'newsletterPopup.subscribed': 'Erfolgreich abonniert!',
        'couponPopup.text': 'Nur diese Woche - spare 20% auf jedes Abo mit dem Gutscheincode Jetzt-KI-lernen20',
        'couponPopup.offerLine': 'Nur diese Woche - spare 20% auf jedes Abo',
        'couponPopup.codeLabel': 'Gutscheincode',
        'couponPopup.expiresIn24Hours': 'Gültig für die nächsten 24 Stunden',
        'couponPopup.getFullAccessCta': 'Jetzt Zugang mit 20% Rabatt sichern',
        'couponPopup.expiresInDays': 'Läuft in 7 Tagen ab',
        'couponPopup.expiresLabel': 'Gutschein endet',
        'couponPopup.expiresDate': '09/03',
        'couponPopup.discountTitle': '20% RABATT',
        'couponPopup.copy': 'Code kopieren',
        'couponPopup.copySuccess': 'Gutscheincode kopiert',
        'couponPopup.copyError': 'Gutscheincode konnte nicht kopiert werden',
        // Footer
        'footer.stayUpdated': 'Bleibe auf dem Laufenden über AI-Tool-Launches & Tutorials.',
        'footer.getEarlyAccess': 'Erhalte frühzeitigen Zugang zu AI-Veranstaltungen, Trend-Tools und Insider-Tipps dein Shortcut zum Meistern der Zukunft.',
        'footer.enterEmail': 'E-Mail eingeben',
        'footer.invalidEmail': 'Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@domain.com)',
        'footer.getStarted': 'Loslegen',
        'footer.description': 'Tools durchsuchen, strukturierte Lektionen ansehen, Lernpfaden folgen & über neueste Tools, Nachrichten und Veranstaltungen auf dem Laufenden bleiben.',
        'footer.affiliatePromoEyebrow': 'Partner mit Airies',
        'footer.affiliatePromoTitle': 'Starte dein wiederkehrendes Einkommen mit dem Airies Affiliate-Programm.',
        'footer.affiliatePromoDescription': 'Entdecke das Affiliate- und Student-Ambassador-Programm für Creator, Communities und Hochschulen, die praxisnahe KI-Bildung weiterempfehlen.',
        'footer.affiliatePromoCta': 'Affiliate-Seite ansehen',
        'footer.company': 'Unternehmen',
        'footer.resources': 'Ressourcen',
        'footer.solutions': 'Lösungen',
        'footer.support': 'Support',
        'footer.legal': 'Rechtliches',
        'footer.allRightsReserved': 'Alle Rechte vorbehalten',
        'footer.allServicesOnline': 'Alle Dienste sind online',
        'footer.privacyPolicy': 'Datenschutzerklärung',
        'footer.terms': 'AGB',

        // Affiliate page
        'affiliate.hero.eyebrow': 'Affiliate-Programm',
        'affiliate.hero.title': 'Verdiene wiederkehrendes Einkommen mit KI-Bildung',
        'affiliate.hero.titleLineOne': 'Verdiene wiederkehrendes Einkommen',
        'affiliate.hero.titleLineTwo': 'durch die Vermittlung von',
        'affiliate.hero.titleHighlight': 'praktischer KI-Bildung.',
        'affiliate.hero.subtitle': 'Tritt dem Airies Affiliate-Programm bei und verdiene monatliche Provisionen, indem du Menschen hilfst, praktische KI-Fähigkeiten zu lernen.',
        'affiliate.hero.highlights.firstPayment': '40 % Provision auf die erste Zahlung',
        'affiliate.hero.highlights.recurring': '20 % wiederkehrende monatliche Provision',
        'affiliate.hero.highlights.referralCode': 'Persönlicher Empfehlungscode',
        'affiliate.hero.highlights.fastGrowing': 'Bewirb eine schnell wachsende KI-Lernplattform',
        'affiliate.hero.ctaAffiliate': 'Affiliate werden',
        'affiliate.hero.ctaAmbassador': 'Student Ambassador werden',
        'affiliate.hero.trustLine': 'Mehr als 10.000 Lernende nutzen bereits Airies',
        'affiliate.hero.cardLabel': 'Wiederkehrendes Umsatzmodell',
        'affiliate.hero.cardRecurringLabel': 'Monatliche wiederkehrende Provision',
        'affiliate.hero.cardRecurringValue': '20 %',
        'affiliate.hero.cardRecurringDescription': 'wiederkehrende monatliche Provision',
        'affiliate.hero.cardFirstPaymentLabel': 'Erste Zahlung',
        'affiliate.hero.cardFirstPaymentValue': '40 %',
        'affiliate.hero.cardFirstPaymentDescription': 'Provision auf die erste Zahlung',
        'affiliate.hero.cardPlatformLabel': 'Abo-Preis',
        'affiliate.hero.cardPlatformValue': '€ 25.90',
        'affiliate.hero.cardPlatformDescription': 'Klarer Preis, leicht zu erklären',
        'affiliate.hero.imageAlt': 'Glücklicher Affiliate prüft Ergebnisse auf Laptop und Smartphone',
        'affiliate.howItWorks.eyebrow': 'So funktioniert es',
        'affiliate.howItWorks.title': 'So funktioniert das Airies Affiliate-Programm',
        'affiliate.howItWorks.subtitle': 'Praktisches, strukturiertes KI-Lernen für neugierige Köpfe, moderne Fachkräfte und zukunftsorientierte Teams.',
        'affiliate.howItWorks.steps.join.title': 'Dem Programm beitreten',
        'affiliate.howItWorks.steps.join.description': 'Bewirb dich als Affiliate oder Student Ambassador.',
        'affiliate.howItWorks.steps.code.title': 'Deinen Empfehlungscode erhalten',
        'affiliate.howItWorks.steps.code.description': 'Du erhältst einen persönlichen Code, den du mit deiner Zielgruppe teilen kannst.',
        'affiliate.howItWorks.steps.earn.title': 'Wiederkehrende Provision verdienen',
        'affiliate.howItWorks.steps.earn.description': 'Jeder zahlende Abonnent, den du vermittelst, bringt dir monatliches Einkommen.',
        'affiliate.earnings.eyebrow': 'Einkommenspotenzial',
        'affiliate.earnings.title': 'Dein wiederkehrendes Einkommenspotenzial',
        'affiliate.earnings.subtitle': 'Airies-Abonnements kosten 25,90 EUR pro Monat und Affiliates erhalten 20 % wiederkehrende Provision.',
        'affiliate.earnings.planPriceLabel': 'Abo-Preis',
        'affiliate.earnings.planPriceValue': '25,90 EUR / Monat',
        'affiliate.earnings.commissionLabel': 'Wiederkehrende Affiliate-Provision',
        'affiliate.earnings.commissionValue': '20 % / Monat',
        'affiliate.earnings.examples.hundred.audience': '100 Abonnenten',
        'affiliate.earnings.examples.hundred.revenue': '100 × 25,90 EUR = 2.590 EUR Monatsumsatz',
        'affiliate.earnings.examples.hundred.payout': '518 EUR / Monat',
        'affiliate.earnings.examples.hundred.payoutLabel': 'Deine wiederkehrende Provision',
        'affiliate.earnings.examples.thousand.audience': '1.000 Abonnenten',
        'affiliate.earnings.examples.thousand.revenue': '1.000 × 25,90 EUR = 25.900 EUR Monatsumsatz',
        'affiliate.earnings.examples.thousand.payout': '5.180 EUR / Monat',
        'affiliate.earnings.examples.thousand.payoutLabel': 'Deine wiederkehrende Provision',
        'affiliate.earnings.examples.tenThousand.audience': '10.000 Abonnenten',
        'affiliate.earnings.examples.tenThousand.revenue': '10.000 × 25,90 EUR = 259.000 EUR Monatsumsatz',
        'affiliate.earnings.examples.tenThousand.payout': '51.800 EUR / Monat',
        'affiliate.earnings.examples.tenThousand.payoutLabel': 'Deine wiederkehrende Provision',
        'affiliate.earnings.note': 'Top-Affiliates können ihre Empfehlungen mit der Zeit ausbauen und ein erhebliches monatliches, wiederkehrendes Einkommen aufbauen.',
        'affiliate.audience.eyebrow': 'Wer mitmachen sollte',
        'affiliate.audience.title': 'Für wen das Airies Affiliate-Programm gedacht ist',
        'affiliate.audience.items.contentCreators': 'KI-Content-Creator',
        'affiliate.audience.items.videoCreators': 'YouTuber und TikTok-Creator',
        'affiliate.audience.items.linkedinCreators': 'LinkedIn-Creator',
        'affiliate.audience.items.newsletterOwners': 'Newsletter-Betreiber',
        'affiliate.audience.items.studentAmbassadors': 'Student Ambassadors',
        'affiliate.audience.items.universityClubs': 'Hochschulgruppen',
        'affiliate.audience.items.consultants': 'Berater und Lehrende',
        'affiliate.audience.items.communities': 'Communities mit Interesse an KI',
        'affiliate.studentAmbassador.eyebrow': 'Campus-Ambassador-Track',
        'affiliate.studentAmbassador.title': 'Studierende können Airies auf dem Campus vertreten',
        'affiliate.studentAmbassador.description': 'Studierende können sich ebenfalls bewerben, Airies auf dem Campus zu repräsentieren und KI-Kompetenz zu fördern.',
        'affiliate.studentAmbassador.benefits.freeAccess': 'Kostenloser Zugang zu Airies',
        'affiliate.studentAmbassador.benefits.personalCode': 'Persönlicher Empfehlungscode',
        'affiliate.studentAmbassador.benefits.commission': 'Provision auf Empfehlungen',
        'affiliate.studentAmbassador.benefits.recognition': 'Ambassador-Anerkennung für Lebenslauf und LinkedIn',
        'affiliate.finalCta.eyebrow': 'Jetzt starten',
        'affiliate.finalCta.title': 'Beginne mit Airies wiederkehrendes Einkommen aufzubauen',
        'affiliate.finalCta.subtitle': 'Bewirb dich als Affiliate oder Student Ambassador und verwandle deine Reichweite in Zielgruppe, Community oder Campus in monatlich wiederkehrendes Einkommen.',
        'affiliate.finalCta.ctaAffiliate': 'Als Affiliate bewerben',
        'affiliate.finalCta.ctaAmbassador': 'Student Ambassador werden',

        // Auth
        'auth.welcomeBack': 'Willkommen zurück 😃',
        'auth.weMissedYou': 'Wir haben dich vermisst, bitte gib deine Daten ein, um dich anzumelden',
        'auth.signInToAccount': 'Melde dich in deinem Konto an',
        'auth.signInWithSSO': 'Mit SSO anmelden',
        'auth.signInWithUniversity': 'Melde dich mit deiner Universitäts-E-Mail an',
        'auth.resetPassword': 'Passwort zurücksetzen & anmelden',
        'auth.backToSignIn': 'Zurück zur Anmeldung',
        'auth.signInToAdmin': 'Als Admin anmelden',
        'auth.signInToAdminDashboard': 'Melde dich an, um auf das Admin-Dashboard zuzugreifen',
        'auth.waitlistTitle': 'Warteliste beitreten 🚀',
        'auth.waitlistDescription': 'Sei die Erste oder der Erste, die/der von unserem Start erfährt. Erhalte frühzeitigen Zugang zu AIRiES.',
        'auth.waitlistSuccessTitle': 'Du bist auf der Warteliste! 🎉',
        'auth.waitlistAlreadyOnListTitle': 'Du stehst bereits auf der Liste!',
        'auth.waitlistSuccessDescription': 'Danke fürs Anmelden! Wir informieren dich, sobald wir starten.',
        'auth.waitlistAlreadyOnListDescription': 'Wir haben deine E-Mail bereits. Wir informieren dich, sobald wir starten!',
        'auth.alreadyHaveSSO': 'Du hast bereits SSO-Zugang?',
        'auth.signInWithUniversityCta': 'Mit deiner Universitäts-E-Mail anmelden',
        'auth.backToHome': 'Zurück zur Startseite',
        'auth.fullNameLabel': 'Vollständiger Name',
        'auth.fullNamePlaceholder': 'Max Mustermann',
        'auth.emailLabel': 'E-Mail-Adresse',
        'auth.emailPlaceholder': 'du@beispiel.de',
        'auth.invalidEmail': 'Bitte gib eine gültige E-Mail-Adresse ein.',
        'auth.fullNameRequired': 'Bitte gib deinen vollständigen Namen ein.',
        'auth.genericError': 'Etwas ist schiefgelaufen, bitte versuche es erneut.',
        'auth.joinWaitlist': 'Warteliste beitreten',
        'auth.or': 'Oder',
        'auth.ssoAccessThroughUniversity': 'Du hast bereits Zugang über deine Universität oder Firma?',
        'auth.corporateLoginTitle': 'Firmen-Login',
        'auth.corporateLoginQuestion': 'Hat dein Unternehmen Zugang zu Airies?',
        'auth.corporateLoginNote': 'Nur hier anmelden, wenn du eine Unternehmens-E-Mail oder E-Mail mit Unternehmens-Domain hast, die bei uns registriert ist.',
        'auth.studentLoginTitle': 'Studenten-Login',
        'auth.studentLoginQuestion': 'Ist deine Universität für kostenlosen Zugang zu Airies berechtigt',
        'auth.studentLoginNote': 'Nur hier anmelden, wenn du eine Studenten-/Universitäts-E-Mail oder E-Mail mit Universitäts-Domain hast, die bei uns registriert ist.',
        'auth.studentLoginSeeList': 'Liste ansehen',
        'auth.individualAccessTitle': 'Individueller Zugang',
        'auth.individualAccessQuestion': 'Möchtest du individuellen Zugang erwerben?',
        'auth.individualAccessDescription': 'und wir informieren dich, sobald dies möglich ist',
        'auth.signUpHere': 'Hier anmelden',
        'auth.signInWithSSOCta': 'Anmelden',
        'auth.universityEmailLabel': 'Universitäts- oder Unternehmens-E-Mail',
        'auth.universityEmailPlaceholder': 'du@universität.ch oder du@unternehmen.com',
        'auth.invalidUniversityEmail': 'Bitte gib eine gültige Universitäts- oder Unternehmens-E-Mail-Adresse ein.',
        'auth.otpAlreadyUsed': 'Dieser Code wurde bereits verwendet. Bitte fordere einen neuen an.',
        'auth.otpExpired': 'Dein Code ist abgelaufen. Bitte fordere einen neuen an.',
        'auth.otpInvalid': 'Der eingegebene Code ist falsch.',
        'auth.passwordTooWeak': 'Das Passwort muss mindestens 8 Zeichen lang sein und Buchstaben sowie Zahlen enthalten.',
        'auth.emailDomainNotAllowed': 'Diese E-Mail-Domain ist nicht erlaubt. Bitte nutze deine Universitäts- oder Unternehmens-E-Mail. Oder beantrage hier Zugang',
        'auth.applyForAccess': 'Hier klicken',
        'auth.accessRequestTitle': 'Zugang beantragen',
        'auth.accessRequestDescription': 'Fülle das untenstehende Formular aus, um Zugang für deine Universität oder dein Unternehmen zu beantragen.',
        'auth.accessRequestSuccess': 'Dein Zugangsantrag wurde erfolgreich übermittelt. Wir werden deinen Antrag prüfen und uns bald bei dir melden.',
        'auth.enterPassword': 'Bitte gib dein Passwort ein.',
        'auth.incorrectEmailOrPassword': 'E-Mail oder Passwort ist falsch.',
        'auth.licenseRequired': 'Bitte gib deinen Lizenzschlüssel ein.',
        'auth.licenseRedeemed': 'Lizenz eingelöst. Fahre mit der Registrierung fort, um den Zugriff zu aktivieren.',
        'auth.licenseValidCorporate': 'Unternehmenslizenz erkannt.',
        'auth.licenseValidIndividual': 'Individuelle Lizenz erkannt.',
        'auth.completeSignup': 'Registrierung abschließen',
        'auth.licenseValidatedIndividual': 'Deine Lizenz wurde validiert. Schließe unten die Kontoeinrichtung ab.',
        'auth.licenseClaimed': 'Lizenz erfolgreich aktiviert!',
        'auth.licenseSeatsRemaining': 'Verbleibende Plätze',
        'auth.redeemTitle': 'Lizenz einlösen',
        'auth.redeemDescription': 'Gib deine E-Mail-Adresse und deinen Lizenzschlüssel ein, um mit dem Onboarding fortzufahren.',
        'auth.licenseKeyLabel': 'Lizenzschlüssel',
        'auth.licenseKeyPlaceholder': 'AIRIES-XXXX-XXXX-XXXX-XXXXX',
        'auth.redeemCta': 'Weiter',
        'auth.redeemHelpText': 'Bereits registriert?',
        'auth.otpRequired': 'Bitte gib den an deine E-Mail gesendeten Code ein.',
        'auth.fullNameRequiredForSignup': 'Bitte gib deinen vollständigen Namen ein.',
        'auth.passwordRequired': 'Bitte gib dein Passwort ein.',
        'auth.passwordRequiredForSignup': 'Bitte erstelle ein Passwort.',
        'auth.newPasswordRequired': 'Bitte erstelle ein neues Passwort.',
        'auth.welcomeBackHeading': 'Willkommen zurück 😃',
        'auth.firstTimeHeading': 'Lass uns dich einrichten 🎓',
        'auth.resetPasswordHeading': 'Setze dein Passwort zurück',
        'auth.welcomeBackSubheading': 'Wir haben dich vermisst, bitte gib deine Daten ein, um dich anzumelden',
        'auth.firstTimeSubheading': 'Wir haben einen 6-stelligen Code an deine Universitäts- oder Unternehmens-E-Mail gesendet',
        'auth.resetPasswordSubheading': 'Gib den Code aus deiner E-Mail ein, um dein Passwort zurückzusetzen',
        'auth.ssoOtpSentStatus': 'Bitte prüfe deine Universitäts- oder Unternehmens-E-Mails auf den 6-stelligen Code.',
        'auth.ssoPasswordStepStatus': 'Willkommen zurück! Bitte gib dein Passwort ein, um fortzufahren.',
        'auth.ssoOtpResetStatus': 'Wir haben dir einen Code per E-Mail gesendet, um dein Passwort zurückzusetzen.',
        'auth.passwordLabel': 'Passwort',
        'auth.rememberMe': 'Angemeldet bleiben',
        'auth.continue': 'Weiter',
        'auth.forgotPassword': 'Passwort vergessen?',
        'auth.passwordPlaceholder': 'Gib dein Passwort ein',
        'auth.showPassword': 'Passwort anzeigen',
        'auth.hidePassword': 'Passwort ausblenden',
        'auth.otpLabel': 'Einmalpasswort',
        'auth.otpPlaceholder': '6-stelliger Code',
        'auth.otpSpamHint': '(Bitte Spam Ordner prüfen)',
        'auth.fullNameSignupLabel': 'Vollständiger Name',
        'auth.fullNameSignupPlaceholder': 'Dieser Name wird für deine Zertifikate verwendet',
        'auth.createPasswordLabel': 'Passwort erstellen',
        'auth.createPasswordPlaceholder': 'Mindestens 8 Zeichen',
        'auth.alternateEmailLabel': 'Alternative E-Mail',
        'auth.alternateEmailPlaceholder': 'du@beispiel.de',
        'auth.verifyAndCreateAccount': 'Bestätigen & Konto erstellen',
        'auth.resetOtpLabel': 'Einmalpasswort',
        'auth.resetOtpPlaceholder': '6-stelliger Code',
        'auth.newPasswordLabel': 'Neues Passwort',
        'auth.newPasswordPlaceholder': 'Erstelle ein sicheres Passwort',
        'auth.continueWithGoogle': 'Mit Google fortfahren',
        'auth.orContinueWithEmail': '— oder mit E-Mail fortfahren —',
        'auth.googleSignInFailed': 'Google-Anmeldung fehlgeschlagen. Bitte versuche es erneut.',
        'auth.companyName': 'Airies Learning Platform',
        'auth.copyright': '© 2026 Airies GmbH',
        'auth.googleSignInSuccess': 'Erfolgreich angemeldet!',
        'auth.completingSignIn': 'Anmeldung wird abgeschlossen...',
        'auth.redirectingToGetStarted': 'Weiterleitung zur Startseite...',
        'auth.redirectingToDashboard': 'Weiterleitung zum Dashboard...',
        'auth.resetPasswordAndSignIn': 'Passwort zurücksetzen & anmelden',
        'auth.backToSignInCta': 'Zurück zur Anmeldung',
        'auth.getStartedTitle': 'Loslegen',
        'auth.getStartedDescription': 'Melde dich an oder erstelle ein Konto, um AIRIES zu nutzen.',
        'auth.discountCodeFullAccess': 'Hast du einen Gutscheincode oder möchtest vollen Zugang?',
        'auth.signUp': 'Registrieren',
        'auth.signIn': 'Anmelden',
        'auth.referralCodeLabel': 'Empfehlungscode',
        'auth.referralCodePlaceholder': 'Optional',
        'auth.referralCodeHelper': 'Hast du einen Empfehlungslink? Er wird automatisch eingefügt.',
        'auth.referralCodeValid': 'Empfehlung angewendet',
        'auth.referralCodeInvalid': 'Ungültiger Empfehlungscode',
        'auth.validatingReferral': 'Wird überprüft...',
        'auth.applyReferral': 'Anwenden',
        'auth.createAccount': 'Konto erstellen',
        'auth.otpVerificationTitle': 'E-Mail bestätigen',
        'auth.otpVerificationDescription': 'Wir haben einen 6-stelligen Code an deine E-Mail gesendet. Bitte gib ihn unten ein.',
        'auth.verifyEmail': 'E-Mail bestätigen',
        'auth.resendOtp': 'Code erneut senden',
        'auth.otpSent': 'Code gesendet!',
        'auth.individual': 'Individuell',
        'auth.studentCorporate': 'Student/Unternehmen',
        'auth.dontHaveAccount': 'Noch kein Konto? ',
        'auth.alreadyHaveAccount': 'Bereits ein Konto? ',
        'auth.licenseInvalid': 'Ungültige Lizenz',
        'auth.licenseSeatsExhausted': 'Alle Plätze für diese Lizenz sind bereits vergeben.',
        'auth.seatsExhaustedContactAdmin': 'Alle Plätze sind derzeit belegt. Bitte kontaktiere deinen Administrator.',
        'auth.licenseEntryTitle': 'E-Mail eingeben',
        'auth.licenseEntryDescription': 'Gib deine E-Mail ein, um mit deiner Lizenz fortzufahren',
        'auth.skipLicense': 'Ohne Lizenz fortfahren',
        'auth.sendResetCode': 'Reset-Code senden',
        'auth.forgotPasswordDescription': 'Gib deine E-Mail ein, um einen Reset-Code zu erhalten',
        'auth.getStarted': 'Loslegen',
        
        // Referral
        'referral.title': 'Empfehlung',
        'referral.description': 'Lade Freunde ein. Wenn berechtigte Empfehlungen abonnieren, erhältst du Belohnungen.',
        'referral.yourReferralLink': 'Dein Empfehlungslink',
        'referral.copyLink': 'Link kopieren',
        'referral.share': 'Teilen',
        'referral.yourReferralCode': 'Dein Empfehlungscode',
        'referral.copyCode': 'Code kopieren',
        'referral.status': 'Status',
        'referral.successfulReferrals': 'Erfolgreiche Empfehlungen',
        'referral.rewardStatus': 'Belohnungsstatus',
        'referral.notEarned': 'Nicht verdient',
        'referral.earned': 'Verdient',
        'referral.linkCopied': 'Kopiert!',
        'referral.codeCopied': 'Kopiert!',
        'referral.failedToLoad': 'Fehler beim Laden der Empfehlungsinformationen',
        'referral.shareTitle': 'AIRIES beitreten',
        'referral.shareDescription': 'Schau dir AIRIES an und starte deine KI-Lernreise!',
        'referral.paidReferrals': 'Bezahlte Empfehlungen',
        'referral.noPaidReferrals': 'Noch keine bezahlten Empfehlungen',
        'referral.rewardsPaused': 'Belohnungen pausiert',

        // Pricing
        'pricing.getStarted': 'Loslegen',
        'pricing.currentPlan': 'Aktueller Plan',
        'pricing.select': 'Auswählen',
        'pricing.comingSoon': 'Demnächst verfügbar',
        'pricing.contactUs': 'Kontaktiere uns',
        'pricing.startLearning': 'Lernen starten',
        'pricing.contactForPricing': 'Kontaktiere uns für Preise',
        'pricing.numberOfUsers': 'Anzahl der Nutzer',
        'pricing.enterValidNumber': 'Bitte gib eine Zahl von 1 oder mehr ein',
        
        // Upgrade Modal
        'upgradeModal.title': 'Upgrade, um alle Ebenen freizuschalten',
        'upgradeModal.body': 'Tritt der Warteliste bei, um frühzeitigen Zugang zu erhalten, wenn Upgrades verfügbar werden.',
        'upgradeModal.joinWaitlist': 'Warteliste beitreten',
        'upgradeModal.notNow': 'Nicht jetzt',
        
        // Waitlist
        'waitlist.title': 'Warteliste beitreten',
        'waitlist.subtitle': 'Erhalte frühzeitigen Zugang, wenn Upgrades verfügbar werden.',
        'waitlist.email': 'E-Mail',
        'waitlist.emailPlaceholder': 'deine@email.de',
        'waitlist.fullName': 'Vollständiger Name',
        'waitlist.fullNamePlaceholder': 'Max Mustermann',
        'waitlist.note': 'Notiz / Interesse',
        'waitlist.notePlaceholder': 'Teile uns mit, was dich am meisten interessiert...',
        'waitlist.optional': 'optional',
        'waitlist.submit': 'Warteliste beitreten',
        'waitlist.submitting': 'Wird übermittelt...',
        'waitlist.success': 'Erfolgreich der Warteliste beigetreten!',
        'waitlist.successTitle': 'Du bist auf der Warteliste!',
        'waitlist.successMessage': 'Wir benachrichtigen dich, wenn Upgrades verfügbar werden.',
        'waitlist.alreadyOnWaitlist': 'Du bist bereits auf der Warteliste!',
        'waitlist.error': 'Fehler beim Beitreten zur Warteliste. Bitte versuche es erneut.',
        'waitlist.backToCourses': 'Zurück zu den Kursen',
        'pricing.paymentsNotEnabled': 'Zahlungen noch nicht aktiviert',
        
        // Hero
        'hero.heading': 'Wo KI auf',
        'hero.headingHighlight': 'Ambition trifft.',
        'hero.subheading': 'Die KI-Lernplattform für Alle - Privatpersonen, Studierende und Unternehmen. Zugänglich, bezahlbar und wirkungsvoll, auch ohne Vorkenntnisse. Registriere dich jetzt für ein kostenloses Probeabo!',
        'hero.startLearning': 'Lernen starten',
        'hero.explorePlatform': 'Plattform erkunden',
        'hero.takeTour': 'Tour starten',
        'hero.requestDemo': 'Demo anfordern',
        'hero.exploreDashboard': 'Erkunde unser Lern-Dashboard in 2 Minuten',
        'hero.productTour': 'Hier ist eine Produkttour, wie Airies funktioniert...',
        'hero.productTourTitle': 'HTML-Produkttour des Airies Lern-Dashboards',
        'hero.kickerNew': 'Lass dich in wenigen Tagen in KI zertifizieren.',
        'hero.headingNew': 'KI-zertifiziert in wenigen Tagen',
        'hero.subheadingNew': 'Vom KI-Einsteiger zum KI-Experten im Job mit praxisnahen Lektionen für den Arbeitsalltag.',
        'hero.ctaPrimary': 'Kostenlos starten',
        'hero.ctaSecondary': 'So funktioniert es (2 Min.)',
        'hero.ctaPrimaryPlan': 'Lerne KI kostenlos',
        'hero.ctaSecondaryPlan': 'Voller Plattformzugang 25,90 EUR/Monat',
        'hero.priceNotePlan': 'Preis für Gründungsmitglieder',
        'hero.ctaHelperLine': '5 kostenlose Kurse • keine Kreditkarte',
        'hero.highlights.secureCheckout': '45+ Kurse\ndie dir wirklich helfen',
        'hero.highlights.moneyBack': '7-Tage-Geld-zurück-\nGarantie',
        'hero.highlights.trustedLearners': 'Vertrauen von über\n10.000 Lernenden',
        'hero.tiles.essentialCourses.title': 'Essenzielle Kurse',
        'hero.tiles.essentialCourses.description': 'Beginne mit über 45 sorgfältig ausgewählten Kursen, die sich auf die KI-Fähigkeiten konzentrieren, die Menschen tatsächlich für ihren Erfolg nutzen.',
        'hero.tiles.moneyGuarantee.title': 'Geld-zurück-Garantie',
        'hero.tiles.moneyGuarantee.description': 'Teste die Plattform ohne Risiko und upgrade erst dann, wenn du bereit für den nächsten Schritt bist.',
        'hero.tiles.trustedLearning.title': 'Vertrauenswürdiges Lernen',
        'hero.tiles.trustedLearning.description': 'Werde Teil von 10.000+ Lernenden, die mit strukturierten Lektionen und klaren Ergebnissen KI sicher anwenden.',
        'hero.postCta.title': 'Starte noch heute mit KI',
        'hero.postCta.subtitle': '3 kostenlose Kurse inklusive',
        'hero.postCta.button': 'Kostenlos starten',
        'hero.statsCompanies': '+500 Unternehmen haben Kosteneinsparungen erzielt',
        'hero.statsUsers': 'Durchschnittlich 1500 Nutzer online',
        'hero.statsGeographies': '200 Regionen abgedeckt',
        'hero.proofStrip': 'Vertrauen von Lernenden in ganz Europa • Genutzt von Fachleuten und Teams • KI-Kurse von Anfänger bis Fortgeschritten',
        'hero.benefit1': 'Lerne KI Schritt für Schritt — ohne technische Vorkenntnisse',
        'hero.benefit2': 'Praktische Tools, Prompts & Workflows',
        'hero.benefit3': 'Zertifikate für Karriere und geschäftliche Nutzung',

        // Official Partners
        'officialPartners.kicker': 'Offizielle Partner',
        'officialPartners.title': 'Airies ist offizieller Partner von OpenAI, Google Cloud und Miro.',
        'officialPartners.description': 'Entdecke partnergestützte KI-Tools, die bereits im Airies Tool-Marktplatz verfügbar sind.',
        'officialPartners.viewPartner': 'Partner-Tool ansehen',
        
        // Who This Is For
        'whoThisIsFor.title': 'Für wen ist das gedacht',
        'whoThisIsFor.students.title': 'Studierende & Einsteiger',
        'whoThisIsFor.students.desc': 'Lerne KI von Grund auf — klar und praxisnah.',
        'whoThisIsFor.students.cta': 'Kurse ansehen',
        'whoThisIsFor.professionals.title': 'Fachleute',
        'whoThisIsFor.professionals.desc': 'Nutze KI, um schneller zu arbeiten, Aufgaben zu automatisieren und vorne dabei zu sein.',
        'whoThisIsFor.professionals.cta': 'Skills erkunden',
        'whoThisIsFor.companies.title': 'Unternehmen',
        'whoThisIsFor.companies.desc': 'Schule Teams sicher und effektiv mit strukturiertem KI-Lernen.',
        'whoThisIsFor.companies.cta': 'Unternehmensschulung',
        'whoThisIsForV2.title': 'Für wen ist das gedacht',
        'whoThisIsForV2.subtitle': 'Praxisnahes, strukturiertes KI-Lernen für neugierige Einsteiger, moderne Professionals und zukunftsorientierte Teams.',
        'whoThisIsForV2.cards.students.title': 'Studierende & neugierige Einsteiger',
        'whoThisIsForV2.cards.students.desc': 'Starte deine KI-Reise bei null - klar, praxisnah und ohne Überforderung. Verstehe, wie KI funktioniert, wie du effektiv promptest und wie du Tools im Alltag und Studium anwendest.',
        'whoThisIsForV2.cards.professionals.title': 'Professionals & Builder',
        'whoThisIsForV2.cards.professionals.desc': 'Nutze KI, um schneller zu arbeiten, repetitive Aufgaben zu automatisieren und neue kreative wie strategische Möglichkeiten zu erschließen. Beherrsche Tools, Workflows und reale Anwendungen für moderne Teams.',
        'whoThisIsForV2.cards.teams.title': 'Teams & Organisationen',
        'whoThisIsForV2.cards.teams.desc': 'Statte dein Team mit strukturiertem KI-Training für sichere, praktische Einführung über Abteilungen hinweg aus. Baue interne KI-Kompetenz auf - ohne das Chaos verteilter Tools und Tutorials.',
        'whoThisIsForV2.ready.title': 'Bereit, deine KI-Reise zu starten?',
        'whoThisIsForV2.ready.subtitle': 'Lerne KI Schritt für Schritt mit strukturierten Lektionen, praktischen Tools und realen Workflows.',
        
        // Trust Strip
        'trustStrip.item1': 'Keine KI- oder Programmier-Vorkenntnisse nötig',
        'trustStrip.item2': 'Lerne in deinem eigenen Tempo',
        'trustStrip.item3': 'Jederzeit kündbar',
        'trustStrip.item4': 'Kostenlos starten — keine Kreditkarte erforderlich',
        
        // Final CTA
        'finalCta.heading': 'Bereit zum Start?',
        'finalCta.cta': 'Kostenlos starten',
        
        // Tools
        'tools.startLearning': 'Lernen starten',
        'tools.getStartedFree': 'Kostenlos loslegen',
        'tools.recentlyAdded': 'Kürzlich hinzugefügt',
        'tools.mostPopular': 'Am beliebtesten',
        'tools.highestRated': 'Am besten bewertet',
        'tools.adjustFilters': 'Passe deine Suche oder Filter an.',
        'tools.discoverTool': 'Tool entdecken',
        'tools.failedToLoad': 'Tools konnten nicht geladen werden',
        'tools.trendingTitle': 'Trending KI-Tools',
        'tools.trendingDescription': 'Entdecke die beliebtesten KI-Tools und bleibe an der Spitze der KI-Revolution.',
        'tools.loading': 'Lädt...',
        'tools.comingSoon': 'Demnächst verfügbar',
        'tools.discoverToolWithArrow': 'Tool entdecken →',
        'toolsByCategory.title': 'Nützliche KI-Tools nach Kategorie',
        'toolsByCategory.viewAll': 'Alle Tools anzeigen →',
        'toolsByCategory.tabsAria': 'Tool-Kategorien',
        'tools.categoryLabel': 'Kategorie',
        'tools.platformLabel': 'Plattform',
        'tools.lastUpdated': 'Zuletzt aktualisiert',
        'tools.visitTool': 'Tool besuchen',
        'tools.courseNotAvailable': 'Kurs nicht verfügbar',

        // Upgrade Required (tool → course)
        'upgradeRequired.title': 'Upgrade erforderlich',
        'upgradeRequired.body': 'Um diesen Kurs zu lernen, musst du einen Plan abonnieren.',
        'upgradeRequired.viewPlans': 'Pläne anzeigen',

        // Tool detail
        'toolDetail.sections.overview': 'Überblick',
        'toolDetail.sections.pricing': 'Preise & Pläne',
        'toolDetail.sections.useCases': 'Anwendungsfälle',
        'toolDetail.sections.prosCons': 'Vorteile & Nachteile',
        'toolDetail.sections.reviews': 'Bewertungen',
        'toolDetail.sections.qa': 'Fragen & Antworten',
        'toolDetail.sections.courses': 'Kurse',
        'toolDetail.sections.releases': 'Versionen / Updates',
        'toolDetail.sections.alternatives': 'Alternativen',
        'toolDetail.sections.links': 'Links & Ressourcen',
        'toolDetail.whyTeamsUseIt': 'Warum Teams es nutzen',
        'toolDetail.pros': 'Vorteile',
        'toolDetail.cons': 'Nachteile',
        'toolDetail.addReview': 'Bewertung hinzufügen',
        'toolDetail.averageRating': 'Durchschnittliche Bewertung',
        'toolDetail.totalReviewsCount': 'Gesamtbewertungen: {{count}}',
        'toolDetail.totalReviewsDescription': 'Gesamtzahl der bisherigen Bewertungen',
        'toolDetail.helpful': 'Hilfreich',
        'toolDetail.reply': 'Antworten',
        'toolDetail.notifyMe': 'Benachrichtige mich',
        'toolDetail.featuresLabel': 'Funktionen:',
        'toolDetail.altRatingSummary': 'Bewertung: {{rating}} ({{likes}} Likes)',
        'toolDetail.vendorWebsite': 'Anbieterseite',
        'toolDetail.loginRequired': 'Bitte melde dich an, um eine Bewertung zu schreiben.',
        'toolDetail.alreadyReviewed': 'Du hast dieses Tool bereits bewertet.',
        'toolDetail.reviewFailed': 'Bewertung konnte nicht gesendet werden.',
        'toolDetail.reviewSuccess': 'Bewertung erfolgreich gesendet.',
        'toolDetail.totalReviews': 'Bewertungen',
        'toolDetail.noReviews': 'Noch keine Bewertungen',
        'toolDetail.noReviewsDescription': 'Sei die Erste oder der Erste, die/der seine Erfahrung teilt.',
        'toolDetail.writeReview': 'Bewertung schreiben',
        'toolDetail.yourRating': 'Deine Bewertung',
        'toolDetail.reviewTitle': 'Titel',
        'toolDetail.reviewTitlePlaceholder': 'Optional – z.B. „Perfekt für Forschungs-Workflows“',
        'toolDetail.reviewComment': 'Kommentar',
        'toolDetail.reviewCommentPlaceholder': 'Beschreibe, wie dir dieses Tool geholfen hat, was dir gefällt und was besser sein könnte.',
        'toolDetail.submitting': 'Wird gesendet...',
        'toolDetail.submitReview': 'Bewertung senden',
        'categories.exploreTitle': 'Nach Kategorien erkunden',
        'categories.description': 'Finde Tools und Lektionen nach dem, was du erreichen möchtest',
        'categories.viewAll': 'Alle Kategorien anzeigen →',
        'categories.viewAllTools': 'Alle Tools anzeigen →',
        'home.courseContent.title': 'Kursinhalte',
        'home.courseContent.subtitle': 'Folge den Levels der Reihe nach - jedes schaltet echte Skills frei, die du im Job nutzen kannst.',
        'home.courseContent.roadmapAlt': 'Kurs-Roadmap',
        'home.courseContent.level1.title': 'Level 1 - Grundlagen der angewandten Intelligenz',
        'home.courseContent.level1.bullet1': 'Verstehe, wie KI "denkt" (ohne Mathe-Kopfschmerzen)',
        'home.courseContent.level1.bullet2': 'Baue das richtige Mindset + Best Practices auf, um schnell zu lernen',
        'home.courseContent.level1.bullet3': 'Meistere Prompting für tägliche Workflows',
        'home.courseContent.level1.bullet4': 'Setze schnell dein erstes Mini-AI-Projekt um',
        'home.courseContent.level2.title': 'Level 2 - Angewandte KI für akademische & berufliche Exzellenz',
        'home.courseContent.level2.bullet1': 'Lerne schneller mit KI-Systemen (statt zufälliger Prompts)',
        'home.courseContent.level2.bullet2': 'Recherchiere besser: tieferes Denken, weniger Rauschen',
        'home.courseContent.level2.bullet3': 'Schreibe bessere Arbeiten/Inhalte mit Klarheit + Struktur',
        'home.courseContent.level2.bullet4': 'Baue Gedächtnis-Systeme, die dir helfen, Wissen zu behalten',
        'home.courseContent.level2.bullet5': 'Erstelle Präsentationen schneller ohne Qualitätsverlust',
        'home.courseContent.level2.bullet6': 'Baue dir mit KI einen echten Karrierevorteil auf',
        'home.courseContent.level3.title': 'Level 3 - Unternehmensweite angewandte Intelligenz & Automatisierung',
        'home.courseContent.level3.bullet1': 'Analysiere deine Zeit + überführe Aufgaben in KI-Workflows',
        'home.courseContent.level3.bullet2': 'Verbessere Kommunikation + Sales-Execution mit KI',
        'home.courseContent.level3.bullet3': 'Skaliere Marketing-Output 10x schneller (ohne cringe zu werden)',
        'home.courseContent.level3.bullet4': 'Baue Reporting & Dashboards mit KI-Unterstützung',
        'home.courseContent.level3.bullet5': 'Automatisiere Meetings + Wissensdokumentation',
        'home.courseContent.level3.bullet6': 'Baue deinen eigenen KI-Assistenten (persönlicher Produktivitäts-Agent)',
        'home.courseContent.level4.title': 'Level 4 - KI-Leadership & skalierbare System-Zertifizierung',
        'home.courseContent.level4.bullet1': 'Designe Teams aus KI-"Agenten" (Rollen + Übergaben)',
        'home.courseContent.level4.bullet2': 'Entscheidungs-Playbooks mit KI-Unterstützung',
        'home.courseContent.level4.bullet3': 'Automatisierung auf Abteilungsebene (wiederholbare Ops, keine Hacks)',
        'home.courseContent.level4.bullet4': 'Lerne, wie KI Produktinnovation vorantreibt',
        'home.courseContent.level4.bullet5': 'Entwickle das Systems-Mindset, um zu skalieren',
        'home.courseContent.level5.title': 'Level 5 - KI für Strategie & Operations',
        'home.courseContent.level5.bullet1': 'Denke in Zukunftsszenarien: Strategie mit KI als Co-Pilot',
        'home.courseContent.level5.bullet2': 'Verbessere Operations mit KI-Optimierung',
        'home.courseContent.level5.bullet3': 'Forecasting + Performance-Intelligence-Workflows',
        'home.courseContent.level5.bullet4': 'Supply-Chain + KPI-Automatisierungskonzepte',
        'home.courseContent.level5.bullet5': 'Executive-Reporting, das sauber, schnell und entscheidungsreif ist',
        'home.courseContent.level6.title': 'Level 6 - Digitale Transformation, Marketing & Sales KI',
        'home.courseContent.level6.bullet1': 'Führe einen digitalen Transformationsplan mit KI-Unterstützung aus',
        'home.courseContent.level6.bullet2': 'Nutze KI-Tools für konsistente Inhalte + Distribution',
        'home.courseContent.level6.bullet3': 'Baue intelligentere Sales-Pipelines + Automatisierung',
        'home.courseContent.level6.bullet4': 'Automatisiere Support + Customer-Experience-Workflows',
        'home.courseContent.level6.bullet5': 'Messe den kommerziellen Impact (damit KI-Arbeit wirklich "zählt")',
        'home.courseContent.level7.title': 'Level 7 - 10× KI für Unternehmer & Business Leader',
        'home.courseContent.level7.bullet1': 'Vom Founder, der Aufgaben erledigt -> zum Operator, der Systeme baut',
        'home.courseContent.level7.bullet2': 'Kundengewinnung + Sales-Workflows mit KI-Unterstützung',
        'home.courseContent.level7.bullet3': 'Marketing-Systeme, die laufen, während du arbeitest',
        'home.courseContent.level7.bullet4': 'Interne Ops + Produktivität in Skalierung',
        'home.courseContent.level7.bullet5': 'Cross-Team-Automatisierungs-Playbooks',
        'home.courseContent.level7.bullet6': 'Baue ein schlankes, KI-gestütztes Unternehmen (realistisch)',
        'home.courseContent.level8.title': 'Level 8 - 10× KI für Finanzen, Trading & Kapitalintelligenz',
        'home.courseContent.level8.bullet1': 'Denke finanziell im KI-Zeitalter (Signale, Narrative, Risiko)',
        'home.courseContent.level8.bullet2': 'Makro- + Marktrecherche-Workflows mit KI',
        'home.courseContent.level8.bullet3': 'Risiko- + Szenario-Design (Entscheidungs-Frameworks)',
        'home.courseContent.level8.bullet4': 'Portfolio-Konstruktions-Workflows',
        'home.courseContent.level8.bullet5': 'Trading-Systeme + Konzepte für disziplinierte Ausführung',
        'home.courseContent.level8.bullet6': 'Baue einen "KI-augmentierten Investor"-Workflow',
        'news.previewTitle': 'Nachrichten & Insights Vorschau',
        'news.previewDescription': 'Bleibe auf dem Laufenden mit Updates, Tutorials und Analysen zu KI-Tools und Hardware.',
        'news.exploreMore': 'Mehr erkunden →',
        'news.defaultLabel': 'Nachrichten',
        'news.noNewsFound': 'Keine Nachrichten gefunden',
        'news.insights': 'INSIGHTS',
        'news.aiAndCreativeExpression': 'KI und kreativer Ausdruck',
        'faq.title': 'Häufig gestellte',
        'faq.titleHighlight': 'Fragen',
        'faq.description': 'Finde Antworten auf alle deine Fragen.',
        'faq.viewMore': 'Mehr anzeigen',
        'faq.viewAll': 'Alle FAQs anzeigen',
        'faq.noFaqsAvailable': 'Derzeit sind keine FAQs verfügbar.',
        'news.noNewsSubtitle': 'Keine Nachrichten verfügbar. Bitte versuche es später erneut.',
        
        // My Courses
        'myCourses.all': 'Alle',
        'myCourses.inProgress': 'In Bearbeitung',
        'myCourses.completed': 'Abgeschlossen',
        'myCourses.searchPlaceholder': 'Kurse durchsuchen...',
        'myCourses.noCoursesFound': 'Keine Kurse gefunden',
        'myCourses.failedToLoad': 'Kurse konnten nicht geladen werden',
        'myCourses.notFound': 'Kurs nicht gefunden',
        'myCourses.courses': 'Kurse',
        'myCourses.recommendedNext': 'Nächste Kurse',
        'myCourses.noRecommendations': 'Lerne weiter, um persönliche Empfehlungen zu sehen.',
        'myCourses.yourLearningJourney': 'Dein Lernverlauf',
        'myCourses.progressAtAGlance': 'Fortschritt im Überblick',
        'myCourses.totalCourses': 'Gesamte Kurse',
        'myCourses.completedCourses': 'Abgeschlossene Kurse',
        'myCourses.inProgressCourses': 'In Bearbeitung Kurse',
        'myCourses.notStartedCourses': 'Nicht gestartete Kurse',
        'myCourses.lockedCourses': 'Gesperrte Kurse',
        'myCourses.statusCurrent': 'Aktuell',
        'myCourses.locked': 'Gesperrt',
        'myCourses.unlock': 'Freischalten',
        'myCourses.progress': 'Fortschritt',
        'myCourses.subtitle': 'Hier findest du deinen Lernfortschritt auf einen Blick.',
        'myCourses.certificateInfo': 'Schließe ein Level ab, um ein Zertifikat zu erhalten',
        'myCourses.level1Title': 'Level 1: Grundlagen der angewandten Intelligenz',
        'myCourses.level2Title': 'Level 2: Angewandte KI für akademische & berufliche Exzellenz',
        'myCourses.level3Title': 'Level 3: Unternehmensweite angewandte Intelligenz & Automatisierung',
        'myCourses.level4Title': 'Level 4: KI-Führung & skalierbare System-Zertifizierung',
        'myCourses.level5Title': 'Level 5: KI für Strategie und Betrieb',
        'myCourses.level6Title': 'Level 6: Digitale Transformation, Marketing & Sales KI',
        'myCourses.level7Title': 'Level 7: 10× KI für Unternehmer & Geschäftsführer',
        'myCourses.level8Title': 'Level 8: 10× KI für Finanzen, Handel & Kapitalintelligenz',
        
        // Community
        'community.recentlyAdded': 'Kürzlich hinzugefügt',
        'community.trending': 'Trending',
        'community.unanswered': 'Unbeantwortet',
        'community.askQuestion': 'Frage stellen',
        'community.postTitlePlaceholder': 'Gib deine Fragentitel ein...',
        'community.postContentPlaceholder': 'Was denkst du?',
        'community.submitQuestion': 'Frage absenden',
        'community.cancel': 'Abbrechen',
        
        // Rewards
        'rewards.viewAll': 'Alle anzeigen',
        'rewards.quizPass': 'Quiz bestanden',
        'rewards.levelComplete': 'Level abgeschlossen',
        'rewards.promptUnlock': 'Prompt freigeschaltet',
        'rewards.bonus': 'Bonus',
        'rewards.reason': 'Freigeschalteter Prompt für absolviertes Level',
        
        // Events
        'events.recentlyAdded': 'Neu hinzugefügt',
        'events.startingSoon': 'Beginnt bald',
        'events.pastEvents': 'Vergangene Veranstaltungen',
        'events.searchPlaceholder': 'Veranstaltungen durchsuchen...',
        
        // Blogs
        'blogs.title': 'Blogs',
        'blogs.subtitle': 'Entdecke Einblicke, Tipps und Geschichten über KI und Technologie',
        'blogs.searchPlaceholder': 'Blogs durchsuchen...',
        'blogs.readMore': 'Weiterlesen',
        'blogs.noBlogsFound': 'Keine Blogs gefunden',
        'blogs.notFound': 'Blog nicht gefunden',
        'blogs.notFoundDesc': 'Der Blogbeitrag, den du suchst, existiert nicht.',
        'blogs.backToBlogs': 'Zurück zu Blogs',
        'blogs.views': 'Aufrufe',
        'blogs.likes': 'Likes',
        'blogs.comments': 'Kommentare',
        
        // Not Found (404)
        'notFound.title': 'Seite nicht gefunden',
        'notFound.description': 'Die gesuchte Seite scheint nicht zu existieren. Wir helfen dir gerne weiter.',
        'notFound.returnHome': 'Zurück zur Startseite',
        'notFound.exploreTools': 'Tools entdecken',
    },
} as const

export function getTranslation(locale: 'en' | 'de', key: TranslationKey): string {
    return translations[locale][key] || translations.en[key] || key
}
